"use client"

import type React from "react"

import { useState, useEffect, useRef, useMemo } from "react"
import { Table, TableBody, TableCell, TableHead, Header, TableRow } from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { EditColumns } from "@/components/edit-columns"
import { EditFieldModal } from "@/components/edit-field-modal"
import { ColumnContextMenu } from "@/components/column-context-menu"
import { GridCell } from "@/components/grid/grid-cell"
import { GridHeader } from "@/components/grid/grid-header"
import { useWorkflow } from "@/context/workflow-context-simple"
import type { WorkflowStage } from "@/types/workflow"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { CustomFilterIcon } from "@/components/custom-filter-icon"
import { defaultBooleanOptions } from './constants'
import { ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Pin, Lock, Info } from 'lucide-react'
import DOMPurify from 'isomorphic-dompurify'

import { ModernDatePicker } from "@/components/modern-date-picker"
import { getLocalizedField } from '@/utils/language-utils'
import { useTranslation } from '@/hooks/use-translation'
import { useLanguage } from '@/context/language-context'
import { useWorkflowApi } from "@/hooks/use-workflow-api"
import { BulkEditModal } from './bulk-edit-modal'
import { getSearchColumnPath, getFacetKeysFromConfiguration, getSelectFieldsFromConfiguration, getDisplayValueFromColumn } from '@/utils/column-type-helpers';
import { MoveToModal } from '@/components/move-to-modal';
import { useCodeValues } from '@/hooks/use-code-values';
import { isBooleanField, isDateField, isNumberField, isSearchableField, isFacetableField, isFilterableField, hasCodeValueSource, isTextListField } from '@/utils/column-type-helpers';
import { WindowRefService } from '@/lib/window-ref-service';
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking';
import { AuditHistoryPanel } from '@/components/audit-history-panel';
import { ChecklistPanel } from '@/components/checklist-panel';
import { MessageTrigger } from '@/components/message-trigger';
import { Clock, MessageCircle } from 'lucide-react';
import { useFeatureFlags } from '@/contexts/feature-flag-context';
import { Z_INDEX } from '@/lib/z-index';
import { processExternalSources, substituteFieldsInUrl, evaluateCondition } from '@/lib/external-source-utils';

interface WorkflowGridProps {
  stage: string
}

// Utility for case-insensitive property lookup
function getCaseInsensitive(row: any, key: string): any {
  if (!row || !key) return undefined;
  const foundKey = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
  return foundKey ? row[foundKey] : undefined;
}

// Helper to get cell value by case-insensitive columnCode converted to camelCase
function getCellValue(item: any, column: any) {
  if (!item || !column || !column.columnCode) return undefined;
  
  // Convert column code to camelCase
  const targetKey = column.columnCode.charAt(0).toLowerCase() + column.columnCode.slice(1);
  // First try exact match
  if (item[targetKey] !== undefined) {
    return item[targetKey];
  }
  
  // Then try case-insensitive match
  const foundKey = Object.keys(item).find(k => k.toLowerCase() === targetKey.toLowerCase());
  return foundKey !== undefined ? item[foundKey] : undefined;
}



// Add this helper at the top of the file (after imports)
function getFacetOptionsForColumn(facets: any, column: any): { value: string; count: number }[] {
  if (!facets || !column) return [];
  
  const searchPath = getSearchColumnPath(column);
  
  // 1. Direct match with search path
  if (Array.isArray(facets[searchPath]) && facets[searchPath].length > 0) {
    return facets[searchPath].filter((opt: { value: string; count: number }) => opt.value !== "");
  }
  // 2. Direct match with column code
  if (Array.isArray(facets[column.columnCode]) && facets[column.columnCode].length > 0) {
    return facets[column.columnCode].filter((opt: { value: string; count: number }) => opt.value !== "");
  }
  // 3. Case-insensitive match
  const foundKey = Object.keys(facets).find(
    k => k.toLowerCase() === searchPath.toLowerCase()
  );
  if (foundKey && Array.isArray(facets[foundKey])) {
    return facets[foundKey].filter((opt: { value: string; count: number }) => opt.value !== "");
  }
  // 4. Try replacing / with nothing (for edge cases)
  const altKey = searchPath.replace(/\//g, '');
  const foundAlt = Object.keys(facets).find(
    k => k.replace(/\//g, '').toLowerCase() === altKey.toLowerCase()
  );
  if (foundAlt && Array.isArray(facets[foundAlt])) {
    return facets[foundAlt].filter((opt: { value: string; count: number }) => opt.value !== "");
  }
  return [];
}

// Helper to get display name for AssignedToUser/Email
function getAssignedUserDisplay(email: string, assignedUsers: { email: string; fullName: string }[]): string {
  const user = assignedUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
  if (user) {
    return user.fullName ? user.fullName.replace(/\w\S*/g, (txt: string) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()) : user.email;
  }
  return email;
}

// For customValues (code/titleEnglish/titleFrench), use opt.code for value and opt.titleEnglish/titleFrench for label
const isCustomValueOption = (opt: any): opt is { code: string; titleEnglish: string; titleFrench: string; isDeleted: boolean } => 'code' in opt && 'titleEnglish' in opt;
const getOptionValue = (opt: any) => isCustomValueOption(opt) ? opt.code : (typeof opt.value === 'string' ? opt.value : String(opt.value ?? ''));
const getOptionLabel = (opt: any, column: any, assignedUsers: any[], language: string) => {
  const value = getOptionValue(opt);
  if (column.columnCode === 'AssignedToUser' || column.columnCode === 'AssignedToUser/Email') {
    return getAssignedUserDisplay(value, assignedUsers);
  }
  if (isCustomValueOption(opt)) {
    return language === 'fr' ? (opt.titleFrench || opt.code) : (opt.titleEnglish || opt.code);
  }
  return typeof opt.label === 'string' ? opt.label : value;
};

// Helper to determine if a column is filtered for the current stage (for blue filter icon)
function isColumnFiltered(currentStageFilters: any[], column: any): boolean {
  const filterObj = currentStageFilters.find(f => f.fieldConfigurationId === column.fieldConfigurationId);
  return !!(filterObj && filterObj.filter && filterObj.filter.length > 0);
}


// Helper to check if we're on desktop (for context menu functionality)
function isDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 768; // Consider 768px+ as desktop
}

// Helper function to safely get column width with fallbacks for fixed columns
function getSafeColumnWidth(columnWidths: Record<string, number>, columnId: string): number {
  // Checkbox column always returns 50px regardless of what's stored
  if (columnId === 'checkbox') {
    return 50;
  }
  // For all other columns (including history and message), use stored width or fallback
  return columnWidths[columnId] || 160;
}

// Add this helper function after the existing helper functions


export function WorkflowGrid({ stage }: WorkflowGridProps) {
  const headerRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);

  const {
    workOrders,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    configuration,
    selectedItems,
    toggleItemSelection,
    selectAllItems,
    deselectAllItems,
    updateWorkOrderField,
    updateWorkOrdersOptimistically,
    isLoadingConfiguration,
    isLoadingWorkOrders,
    isLoadingRows,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    setItemsPerPage,
    stages,
    updateFilter,
    refreshData,
    removeWorkOrder,
    facets,
    assignedUsers,
    stageFilters,
    globalFilters,
    setStageFilters,
    setGlobalFilters,
    workOrdersFacets,
    setSelectedItems,
    setColumnWidths: setColumnWidthsFromContext,
    state,
    setPinnedColumn,
  } = useWorkflow()

  // State management
  const [isEditColumnsOpen, setIsEditColumnsOpen] = useState(false)
  const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false)
  const [editFieldConfiguration, setEditFieldConfiguration] = useState<any>(null)
  const [isBrowser, setIsBrowser] = useState(false)
  const [editingCell, setEditingCell] = useState<{ rowId: string; columnId: string } | null>(null)
  const [contextMenu, setContextMenu] = useState<{ position: { top: number; left: number }; columnId: string } | null>(
    null,
  )
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  // State for filter popup
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null)
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(null)
  // Checklist panel state
  const [checklistPanelState, setChecklistPanelState] = useState<{
    isOpen: boolean;
    itemId: string;
    workOrderId: string;
    fieldConfigurationId: string;
    itemTitle?: string;
    canEdit?: boolean;
  } | null>(null)

  // Action menu state for right-click row actions
  const [rowActionMenu, setRowActionMenu] = useState<
    | { open: boolean; fboId?: string; obId?: string; rowId: string; x: number; y: number }
    | null
  >(null)
  const [hoveredHeader, setHoveredHeader] = useState<string | null>(null)
  const [headerHeight, setHeaderHeight] = useState(0);
  const [paginationHeight, setPaginationHeight] = useState(0);
  // Global filter sidebar state
  const [isGlobalFilterSidebarOpen, setIsGlobalFilterSidebarOpen] = useState(false);
  // Drawer width constant
  const FILTER_DRAWER_WIDTH = 360;
  // For smooth closing animation
  const [drawerVisible, setDrawerVisible] = useState(false);
  // State for global filter search
  const [globalFilterSearch, setGlobalFilterSearch] = useState("");
  // Ensure sortState is defined as local state
  const [sortState, setSortState] = useState<{ columnCode: string | null, direction: 'asc' | 'desc' | null }>({ columnCode: null, direction: null });
  // Ensure dateFilter is defined as local state
  const [dateFilter, setDateFilter] = useState<{ start: string | null, end: string | null }>({ start: null, end: null });
  // Restore previous working sidebar state and handlers
  const [globalFilterSelections, setGlobalFilterSelections] = useState<Record<string, string[]>>({});
  const [globalFilterShowCount, setGlobalFilterShowCount] = useState<Record<string, number>>({});
  // Add facetsFetched state
  const [facetsFetched, setFacetsFetched] = useState(false);
  // Add local state for popover filter values
  const [popoverFilterValues, setPopoverFilterValues] = useState<Record<string, any>>({});
  // Add this at the top level of the WorkflowGrid component
  const fetchingFacets = useRef(false);
  // Bulk edit modal state
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  // Add state for MoveTo modal
  const [isMoveToOpen, setIsMoveToOpen] = useState(false);
  // Add local state for popover search
  const [popoverSearch, setPopoverSearch] = useState<string>("");
  // Add state to track if initial height calculation is complete
  const [isHeightCalculated, setIsHeightCalculated] = useState(false);
  // Add ref to track if we're just changing pages (vs changing stage/filters)
  const isJustPageChange = useRef(false);
  // Add state for audit history panel
  const [auditHistoryPanel, setAuditHistoryPanel] = useState<{
    isOpen: boolean;
    itemId: string;
    itemTitle?: string;
  } | null>(null);
  
  // Add state for message sidebar monitoring
  const [isMessageSidebarOpen, setIsMessageSidebarOpen] = useState(false);
  // Add state to track which message cell was clicked
  const [clickedMessageRowId, setClickedMessageRowId] = useState<string | null>(null);
  
  // Tracking
  const { trackWorkflowEvent, trackFilterAction, trackGridAction } = useWorkflowTracking();
  // Add state to track previous stage and columns for smooth transitions
  const [previousStage, setPreviousStage] = useState<string | null>(null);
  const [previousColumns, setPreviousColumns] = useState<any[]>([]);
  const [isStageTransitioning, setIsStageTransitioning] = useState(false);
  // Add refs to track previous state without causing re-renders
  const previousStageRef = useRef<string | null>(null);
  const previousColumnsRef = useRef<any[]>([]);

  // Add state for search suggestions
  const [suggestions, setSuggestions] = useState<Record<string, any[]>>({});
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<Record<string, boolean>>({});
  const suggestionsDebounceTimeout = useRef<Record<string, NodeJS.Timeout>>({});
  // Add flag to track when a suggestion was selected
  const [selectedFromSuggestions, setSelectedFromSuggestions] = useState<Record<string, boolean>>({});
  // Draft values for searchable inputs to avoid parent re-render/caret jump
  const searchableDraftRef = useRef<Record<string, string>>({});

  // State for resizable columns - using refs for global event listeners
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const resizingColumnRef = useRef<string | null>(null);
  
  // Keep useState versions for React rendering
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);

  const { t } = useTranslation();
  const { language } = useLanguage();
  const { searchWorkflowItems, fetchSearchSuggestions } = useWorkflowApi();
  const { isFeatureEnabled } = useFeatureFlags();
  const codeValues = useCodeValues();

  // Feature flag to hide message column
  const isMessageColumnHidden = !isFeatureEnabled('messages');

  // Pending items filter constants
  const pendingStatusValues = useMemo(
    () => [
      "Draft",
      "PendingReview",
      "Recalled",
      "ComplianceDraft",
      "RecalledCompliance",
    ],
    []
  );

  // Helper function to fetch suggestions for a specific field
  const fetchSuggestionsForField = async (field: string, searchText: string) => {
    if (!searchText || searchText.trim().length < 2) {
      setSuggestions(prev => ({ ...prev, [field]: [] }));
      return;
    }

    setIsLoadingSuggestions(prev => ({ ...prev, [field]: true }));
    
    try {
      const response = await fetchSearchSuggestions(searchText.trim(), field);
      // Handle different response structures
      let suggestionsArray: any[] = [];
      
      if (Array.isArray(response)) {
        // Direct array response
        suggestionsArray = response;
      } else if (response && response.value && Array.isArray(response.value)) {
        // Response with 'value' property (your backend format)
        suggestionsArray = response.value;
      }
      
      const formattedSuggestions = suggestionsArray.map(suggestion => 
        typeof suggestion === 'string' ? suggestion : (suggestion.text || suggestion)
      );
      
      setSuggestions(prev => ({ 
        ...prev, 
        [field]: formattedSuggestions
      }));
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setSuggestions(prev => ({ ...prev, [field]: [] }));
    } finally {
      setIsLoadingSuggestions(prev => ({ ...prev, [field]: false }));
    }
  };

  // Debounced search function
  const debouncedSearch = (field: string, searchText: string) => {
    // Clear existing timeout for this field
    if (suggestionsDebounceTimeout.current[field]) {
      clearTimeout(suggestionsDebounceTimeout.current[field]);
    }

    // Set new timeout
    suggestionsDebounceTimeout.current[field] = setTimeout(() => {
      fetchSuggestionsForField(field, searchText);
    }, 1000); // 1 second delay
  };

  // Find the current stage configuration
  const stageConfig = configuration?.stageConfigurations.find(
    (s) => s.stageCode.toLowerCase() === stage?.toLowerCase()
  )
  
  const columns = stageConfig?.fieldConfigurations || []
  
  // Check if History and Message columns exist in the fieldConfigurations
  const historyColumn = columns.find(col => col.columnCode === 'History');
  const messageColumn = columns.find(col => col.columnCode === 'Message');
  
  // Mark action columns with special properties
  if (historyColumn) {
    (historyColumn as any).isActionColumn = true;
    (historyColumn as any).actionType = 'history';
    (historyColumn as any).isStaticPosition = true; // Mark as static position
    historyColumn.isSortable = false;
    historyColumn.isFacetable = false;
    historyColumn.isFilterable = false;
    historyColumn.isSearchable = false;
    historyColumn.isEditable = false;
  }
  
  if (messageColumn && !isMessageColumnHidden) {
    (messageColumn as any).isActionColumn = true;
    (messageColumn as any).actionType = 'message';
    (messageColumn as any).isStaticPosition = true; // Mark as static position
    messageColumn.isSortable = false;
    messageColumn.isFacetable = false;
    messageColumn.isFilterable = false;
    messageColumn.isSearchable = false;
    messageColumn.isEditable = false;
  }
  
  // Arrange columns with Message and History always first (after checkbox)
  let displayColumns: any[] = [];
  if (isStageTransitioning && previousColumns.length > 0) {
    // Reapply the same column arrangement logic to previousColumns
    // Filter out message and history columns from previous columns to avoid duplicates
    const regularColumns = previousColumns.filter(col => col.columnCode !== 'Message' && col.columnCode !== 'History');
    
    // Add fixed columns in order: Message, History, then regular columns
    const fixedColumns = [];
    if (messageColumn && !isMessageColumnHidden) {
      fixedColumns.push(messageColumn);
    }
    if (historyColumn) {
      fixedColumns.push(historyColumn);
    }
    displayColumns = [...fixedColumns, ...regularColumns];
  } else {
    // Filter out message and history columns from regular columns
    const regularColumns = columns.filter(col => col.columnCode !== 'Message' && col.columnCode !== 'History');
    
    // Add fixed columns in order: Message, History, then regular columns
    const fixedColumns = [];
    if (messageColumn && !isMessageColumnHidden) {
      fixedColumns.push(messageColumn);
    }
    if (historyColumn) {
      fixedColumns.push(historyColumn);
    }
    displayColumns = [...fixedColumns, ...regularColumns];
  }
  const editableFields = (configuration?.fieldsInUse || []).filter(col => col.isEditable);

  // Initialize browser state and column widths
  useEffect(() => {
    setIsBrowser(true)
    
    // Load saved column widths from view state or use defaults
    const savedWidths = state.columnWidths || {};
    
    const initialWidths: Record<string, number> = {}
    
    // Set checkbox column to fixed width
    const checkboxWidth = 50;
    initialWidths['checkbox'] = checkboxWidth;
    
    // Set default width for action columns (history and message) if they exist in configuration
    const actionColumnWidth = 50;
    if (historyColumn) {
      initialWidths[historyColumn.fieldConfigurationId] = savedWidths[historyColumn.fieldConfigurationId] || actionColumnWidth;
    }
    if (messageColumn && !isMessageColumnHidden) {
      initialWidths[messageColumn.fieldConfigurationId] = savedWidths[messageColumn.fieldConfigurationId] || actionColumnWidth;
    }
    
    // Use all fields from fieldsInUse instead of just current stage columns
    const allFields = configuration?.fieldsInUse || [];
    
    allFields.forEach((field) => {
      // Set default width based on column type or content
      let defaultWidth = 190;
      if (field.columnCode === 'AssignedToUser' || field.columnCode?.includes('Email') || field.columnCode?.includes('Name')) {
        defaultWidth = 230;
      } else if (field.columnTypeCode?.toLowerCase() === 'boolean') {
        defaultWidth = 150;
      } else if (field.columnTypeCode?.toLowerCase() === 'date') {
        defaultWidth = 170;
      } else if (field.columnTypeCode?.toLowerCase() === 'number') {
        defaultWidth = 130;
      }
      
      initialWidths[field.fieldConfigurationId] = savedWidths[field.fieldConfigurationId] || defaultWidth
    })
    
    setColumnWidths(initialWidths)
  }, [configuration?.fieldsInUse, state.columnWidths, isMessageColumnHidden, historyColumn, messageColumn])

  // Resizable column handlers
  const handleResizeStart = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const currentWidth = columnWidths[columnId] || 160;
    
    // Update both refs and state
    isResizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = currentWidth;
    resizingColumnRef.current = columnId;
    
    setIsResizing(true);
    setResizingColumn(columnId);
    
    // Add global mouse event listeners
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!isResizingRef.current || !resizingColumnRef.current) return;
    
    const deltaX = e.clientX - resizeStartXRef.current;
    const newWidth = Math.max(50, resizeStartWidthRef.current + deltaX); // Minimum width of 50px
    
    // Directly manipulate DOM for immediate visual feedback
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const table = tableContainer.querySelector('table');
      if (table) {
        // Update table width immediately to prevent auto-resizing
        const checkboxWidth = 50;
        const currentDataColumnsWidth = displayColumns.reduce((total, column) => {
          if (column.fieldConfigurationId === resizingColumnRef.current) {
            return total + newWidth; // Use the new width for the column being resized
          }
          return total + (getSafeColumnWidth(columnWidths, column.fieldConfigurationId));
        }, 0);
        const totalWidth = checkboxWidth + currentDataColumnsWidth;
        table.style.width = `${totalWidth}px`;
        table.style.minWidth = `${totalWidth}px`;
        
        const headerCells = table.querySelectorAll('th');
        const bodyCells = table.querySelectorAll('td');
        
        // Find the column index - accounting for checkbox (0) and dynamic columns
        let columnIndex = -1;
        if (resizingColumnRef.current === 'checkbox') {
          columnIndex = 0;
        } else {
          // Find the index in displayColumns and add 1 for checkbox
          columnIndex = displayColumns.findIndex(col => col.fieldConfigurationId === resizingColumnRef.current) + 1;
        }
        
        if (columnIndex >= 0) {
          // Update header cell
          if (headerCells[columnIndex]) {
            headerCells[columnIndex].style.width = `${newWidth}px`;
            headerCells[columnIndex].style.minWidth = `${newWidth}px`;
            headerCells[columnIndex].style.maxWidth = `${newWidth}px`;
          }
          
          // Update all body cells in this column
          const totalColumns = displayColumns.length + 1; // +1 for checkbox
          for (let i = 0; i < bodyCells.length; i++) {
            const cellIndex = i % totalColumns;
            if (cellIndex === columnIndex) {
              bodyCells[i].style.width = `${newWidth}px`;
              bodyCells[i].style.minWidth = `${newWidth}px`;
              bodyCells[i].style.maxWidth = `${newWidth}px`;
            }
          }
        }
      }
    }
  };

  const handleResizeEnd = (e?: MouseEvent) => {
    if (!isResizingRef.current || !resizingColumnRef.current) return;
    
    // Calculate final width
    const deltaX = (e?.clientX || 0) - resizeStartXRef.current;
    const finalWidth = Math.max(50, resizeStartWidthRef.current + deltaX);
    
    // Update React state to trigger re-render
    const updatedWidths = {
      ...columnWidths,
      [resizingColumnRef.current!]: finalWidth
    };
    setColumnWidths(updatedWidths);
    
    // Save column widths to view state via context, ensuring checkbox is always fixed
    const widthsToSave = {
      ...updatedWidths,
      checkbox: 50, // Always keep checkbox at 50px
    };
    setColumnWidthsFromContext(widthsToSave);
    
    // Reset both refs and state
    isResizingRef.current = false;
    resizingColumnRef.current = null;
    setIsResizing(false);
    setResizingColumn(null);
    
    // Remove global event listeners
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, []);

  // Force table layout update when column widths change
  useEffect(() => {
    // Don't interfere with resize operations
    if (isResizing || Object.keys(columnWidths).length === 0) return;
    
    // Force a re-render by updating the table container
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.style.width = '100%';
      tableContainer.style.minWidth = '100%';
    }
    
    // Apply column widths directly to DOM elements
    const table = tableContainer?.querySelector('table');
    if (table) {
      // Calculate and set total table width
      const checkboxWidth = 50;
      const dataColumnsWidth = displayColumns.reduce((total, column) => {
        return total + (getSafeColumnWidth(columnWidths, column.fieldConfigurationId));
      }, 0);
      const totalWidth = checkboxWidth + dataColumnsWidth;
      
      table.style.width = `${totalWidth}px`;
      table.style.minWidth = `${totalWidth}px`;
      
      // Apply widths to header cells
      const headerCells = table.querySelectorAll('th');
      headerCells.forEach((cell, index) => {
        if (index === 0) {
          // Checkbox column - fixed width
          cell.style.width = '50px';
          cell.style.minWidth = '50px';
          cell.style.maxWidth = '50px';
        } else {
          // Dynamic columns (history, message, and data columns)
          const column = displayColumns[index - 1]; // -1 for checkbox
          if (column) {
            const width = getSafeColumnWidth(columnWidths, column.fieldConfigurationId);
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
            cell.style.maxWidth = `${width}px`;
          }
        }
      });
      
      // Apply widths to body cells
      const bodyCells = table.querySelectorAll('td');
      bodyCells.forEach((cell, index) => {
        const columnIndex = index % (displayColumns.length + 1); // +1 for checkbox
        if (columnIndex === 0) {
          // Checkbox column - fixed width
          cell.style.width = '50px';
          cell.style.minWidth = '50px';
          cell.style.maxWidth = '50px';
        } else {
          // Dynamic columns (history, message, and data columns)
          const column = displayColumns[columnIndex - 1]; // -1 for checkbox
          if (column) {
            const width = getSafeColumnWidth(columnWidths, column.fieldConfigurationId);
            cell.style.width = `${width}px`;
            cell.style.minWidth = `${width}px`;
            cell.style.maxWidth = `${width}px`;
          }
        }
      });
    }
  }, [columnWidths, displayColumns, isResizing]);

  // Calculate pagination info
  const totalPages = Math.ceil(totalCount / (state.itemsPerPage !== null ? state.itemsPerPage : pageSize))

  // Add this effect to update page size based on window height and available space
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Calculate initial page size with a longer delay to ensure DOM is fully ready
    const initialTimeout = setTimeout(() => {
      // The calculation will be handled by the refs useEffect
    }, 200);
    
    // Add window resize listener with debouncing
    let resizeTimeout: NodeJS.Timeout;
    let lastWindowWidth = window.innerWidth;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const currentWidth = window.innerWidth;
                
        // Reset height calculation flag on any resize to allow recalculation
        setIsHeightCalculated(false);
        lastWindowWidth = currentWidth;
        // The calculation will be handled by the refs useEffect
      }, 200); // Increased debounce time for width changes
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      clearTimeout(initialTimeout);
    };
  }, [headerHeight, paginationHeight, pageSize, setPageSize, setCurrentPage]);

  // Only reset stageFilters on stage change
  useEffect(() => {
    setSortState({ columnCode: null, direction: null });
    // Reset height calculation flag when stage changes
    setIsHeightCalculated(false);
  }, [stage]);

  // Trigger height calculation when refs are available
  useEffect(() => {
    if (!isHeightCalculated) {
      // Add a small delay to ensure layout has settled after width changes
      const timeoutId = setTimeout(() => {
        // Force a layout recalculation to get accurate dimensions
        if (mainContainerRef.current) {
          mainContainerRef.current.offsetHeight; // Force reflow
        }
        if (tableContainerRef.current) {
          tableContainerRef.current.offsetHeight; // Force reflow
        }
        
        // Recalculate page size when refs become available
        const mainContainer = mainContainerRef.current;
        if (mainContainer) {
          const containerRect = mainContainer.getBoundingClientRect();
          const actualContainerHeight = containerRect.height;
          
          // Get actual table header height from the DOM
          const tableHeader = tableContainerRef.current?.querySelector('thead');
          const actualTableHeaderHeight = tableHeader ? tableHeader.getBoundingClientRect().height : 50;
          
          const tableHeaderHeight = headerHeight || actualTableHeaderHeight;
          const paginationHeightActual = paginationHeight || 50;
          const buffer = 40; // Increased buffer to prevent page scroll
          let availableHeight = Math.max(0, actualContainerHeight - tableHeaderHeight - paginationHeightActual - buffer);
          
          if (actualContainerHeight < 100) {
            const windowHeight = window.innerHeight;
            const estimatedTopBarHeight = 120; // Increased to be more conservative
            const estimatedBottomPadding = 50; // Increased to be more conservative
            availableHeight = Math.max(0, windowHeight - estimatedTopBarHeight - estimatedBottomPadding - tableHeaderHeight - paginationHeightActual);
          }
          
          const rowHeight = 48;
          const minRowsPerPage = 5;
          const maxRowsPerPage = Math.floor((window.innerHeight * 0.75) / rowHeight); // Reduced to 75% to be more conservative
          // Add a small buffer to ensure we don't show partial rows
          const bufferForPartialRows = 8; // 8px buffer to prevent showing partial rows
          const calculatedRowsPerPage = Math.floor((availableHeight - bufferForPartialRows) / rowHeight);
          const rowsPerPage = Math.max(minRowsPerPage, Math.min(calculatedRowsPerPage, maxRowsPerPage));
          
          if (rowsPerPage > 0 && rowsPerPage !== pageSize) {
            setCurrentPage(1);
            setPageSize(rowsPerPage);
          }
          setIsHeightCalculated(true);
        } else {
          // Fallback to window-based calculation if refs are not available
          const windowHeight = window.innerHeight;
          const estimatedTopBarHeight = 120; // Increased to be more conservative
          const estimatedBottomPadding = 50; // Increased to be more conservative
          const tableHeaderHeight = headerHeight || 50;
          const paginationHeightActual = paginationHeight || 50;
          const availableHeight = Math.max(0, windowHeight - estimatedTopBarHeight - estimatedBottomPadding - tableHeaderHeight - paginationHeightActual);
          
          const rowHeight = 48;
          const minRowsPerPage = 5;
          const maxRowsPerPage = Math.floor((window.innerHeight * 0.75) / rowHeight); // Reduced to 75% to be more conservative
          // Add a small buffer to ensure we don't show partial rows
          const bufferForPartialRows = 8; // 8px buffer to prevent showing partial rows
          const calculatedRowsPerPage = Math.floor((availableHeight - bufferForPartialRows) / rowHeight);
          const rowsPerPage = Math.max(minRowsPerPage, Math.min(calculatedRowsPerPage, maxRowsPerPage));

          if (rowsPerPage > 0 && rowsPerPage !== pageSize) {
            setCurrentPage(1);
            setPageSize(rowsPerPage);
          }
          setIsHeightCalculated(true);
        }
      }, 100); // 100ms delay to ensure layout has settled
      
      return () => clearTimeout(timeoutId);
    }
  }, [mainContainerRef.current, tableContainerRef.current, isHeightCalculated, headerHeight, paginationHeight, pageSize, setPageSize, setCurrentPage]);

  // Detect stage changes and preserve old state during transitions
  useEffect(() => {
    
    if (previousStageRef.current && previousStageRef.current !== stage) {
      setIsStageTransitioning(true);
      setPreviousColumns(previousColumnsRef.current);
    } else if (!previousStageRef.current) {
      setIsStageTransitioning(false);
    }
    
    // Update refs
    previousStageRef.current = stage;
    previousColumnsRef.current = columns;
    setPreviousStage(stage);
  }, [stage, columns]);

  // Clear transition flag when data is loaded
  useEffect(() => {    
    if (isStageTransitioning && !isLoadingWorkOrders && !isLoadingRows && !isLoadingConfiguration) {

      setIsStageTransitioning(false);
    }
  }, [isStageTransitioning, isLoadingWorkOrders, isLoadingRows, isLoadingConfiguration]);

  // Helper to update or add a filter for a column
  function updateStageFilter(fieldConfigurationId: string, filter: string, values: any) {
    const stageId = stageConfig?.stageId;
    if (!stageId) return;
    const existingFilters = stageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === fieldConfigurationId));
    if (filter) {
      setStageFilters([...existingFilters, { stageId, fieldConfigurationId, filter, values }]);
    } else {
      setStageFilters(existingFilters);
    }
  }

  // Get current stage filters
  const currentStageFilters = useMemo(() => stageFilters.filter(f => f.stageId === stageConfig?.stageId), [stageFilters, stageConfig]);


  // Helper function to get current orderBy value from sortState
  const currentOrderBy = useMemo(() => {
    if (sortState.columnCode && sortState.direction) {
      const sortCol = columns.find(c => c.columnCode === sortState.columnCode);
      if (sortCol) {
        return `${getSearchColumnPath(sortCol)} ${sortState.direction}`;
      }
    }
    return undefined;
  }, [sortState, columns]);

  // Helper function to build filters and trigger data fetch
  const triggerDataFetch = () => {
    if (!isHeightCalculated) return;
    
    // Skip automatic trigger if handling client filter change
    if (state.isHandlingClientFilterChange) return;
    
    // Build filter string from globalFilters and currentStageFilters
    const allFilters = [...globalFilters, ...currentStageFilters].map(f => f.filter).filter(Boolean);
    const baseFilter = `Stage eq '${stage}'`;
    const filterArray = allFilters.length > 0 
      ? [`${baseFilter} and ${allFilters.join(' and ')}`]
      : [baseFilter];
    // Always include all global filter columns in facets
    const facetKeys = getFacetKeysFromConfiguration(configuration);
    if (typeof refreshData === 'function') {
      refreshData(filterArray, currentOrderBy, facetKeys);
    }
  };

  // Main data-fetching effect: combine globalFilters and stageFilters
  useEffect(() => {
    triggerDataFetch();
    // Reset the page change flag after data fetch with a small delay
    if (isJustPageChange.current) {
      setTimeout(() => {
        isJustPageChange.current = false;
      }, 100);
    }
  }, [isHeightCalculated, currentStageFilters, globalFilters, sortState, stage, currentPage, pageSize, state.itemsPerPage, configuration]);



  // Helper to build the final filter string
  function buildFinalFilter(fieldConfigurationId: string, filterString: string) {
    if (!filterString) return '';
    // Don't include Stage in column filters as it's handled in the main effect
    if (filterString.includes('Stage eq')) return filterString;
    return filterString;
  }


  const handleCellEdit = (rowId: string, columnId: string) => {
    // Find the column configuration
    const column = displayColumns.find(col => col.fieldConfigurationId === columnId);
    
    // Check if this is a checklist column
    if (column && column.columnTypeCode?.toLowerCase() === 'checklist') {
      // Find the work order for this row
      const workOrder = workOrders.find(wo => wo.id === rowId);
      if (workOrder) {
        // Check edit permissions
        const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode);
        const canEditFieldData = authorizedActions.includes("EditFieldData");
        
        setChecklistPanelState({
          isOpen: true,
          itemId: rowId,
          workOrderId: workOrder.fboWorkOrderNumber || workOrder.workOrderId,
          fieldConfigurationId: columnId,
          itemTitle: workOrder.workerFullName || workOrder.workerName || 'Worker',
          canEdit: canEditFieldData,
        });
      }
      return;
    }
    
    // Normal cell editing
    setEditingCell({ rowId, columnId })
  }

  const handleCellSave = async (rowId: string, columnId: string, value: any) => {
    
    // Special handling for Actions column - update Stage field instead
    const fieldToUpdate = columnId.toLowerCase() === "actions" ? "Stage" : columnId;
    
    // Convert field name to camelCase to match the data structure (same as getCellValue)
    const camelCaseField = fieldToUpdate.charAt(0).toLowerCase() + fieldToUpdate.slice(1);
  
    
    // If this is a stage update and the stage is changing
    if (fieldToUpdate === "Stage" && value !== stage) {
      // Make the API call for stage update
      const success = await updateWorkOrderField(rowId, fieldToUpdate, value);
      
      // Only remove the item from the current view if the update was successful
      if (success) {
        removeWorkOrder(rowId);
      }
    } else {
      // For non-stage updates, make the API call (non-optimistic)
      await updateWorkOrderField(rowId, camelCaseField, value);
    }
    
    setEditingCell(null);
  }

  const handleColumnHeaderRightClick = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault()
    setContextMenu({
      position: { top: e.clientY, left: e.clientX },
      columnId,
    })
  }

  const handleEditFieldAccess = (column: any) => {
    setEditFieldConfiguration(column)
    setIsEditFieldModalOpen(true)
  }

  // Helper function to get user initials
  const getUserInitials = (user: any) => {
    const fullName = user.fullName || user.email || '';
    if (user.fullName) {
      // Get initials from full name
      return fullName
        .split(' ')
        .filter((name: string) => name.length > 0)
        .map((name: string) => name.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    } else {
      // Get initials from email
      return user.email ? user.email.charAt(0).toUpperCase() : '?';
    }
  }

  const handlePreviousPage = () => {
    if (hasPreviousPage && currentPage > 1) {
      // Track pagination action
      trackGridAction('page', { direction: 'previous', fromPage: currentPage, toPage: currentPage - 1 });
      
      // Update page - useEffect will handle data fetching
      isJustPageChange.current = true;
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (hasNextPage && currentPage < totalPages) {
      // Track pagination action
      trackGridAction('page', { direction: 'next', fromPage: currentPage, toPage: currentPage + 1 });
      
      // Update page - useEffect will handle data fetching
      isJustPageChange.current = true;
      setCurrentPage(currentPage + 1)
    }
  }

  // Add safety check for workOrders to prevent undefined errors
  const safeItems: any[] = workOrders || []
  const isAllSelected = safeItems.length > 0 && safeItems.every((item: any) => item.id && selectedItems.includes(item.id))
  // Compute if any filters are active (stage or global)
  const hasActiveFilters = currentStageFilters.length > 0 || globalFilters.length > 0;

  // Normalize external sources to camelCase for robust mapping
  const externalSources = (configuration?.externalSourceConfigurations || []).map(src => ({
    link: (src as any).link || (src as any).Link,
    titleEnglish: (src as any).titleEnglish || (src as any).TitleEnglish,
    titleFrench: (src as any).titleFrench || (src as any).TitleFrench,
    condition: (src as any).condition || (src as any).Condition,
  })).filter(src => {
    // Filter out sources that don't have required properties
    const isValid = src.link && src.titleEnglish;
    if (!isValid) {
      console.warn('External source missing required properties:', src);
    }
    return isValid;
  })

  // When a popover opens, initialize local state for that column
  const handleFilterIconClick = (e: React.MouseEvent<HTMLButtonElement>, columnCode: string) => {
    const col = columns.find(c => c.columnCode === columnCode);
    if (!col) return;
    
    // Track filter icon click
    trackFilterAction('modify', 'column_filter', columnCode);
    
    const filterObj = currentStageFilters.find(f => f.fieldConfigurationId === col.fieldConfigurationId);
    
    // Set the filter values synchronously before opening the popover
    let newFilterValues = { ...popoverFilterValues };
    
    // Check if column is facetable first - it takes priority over searchable
    const isFilterableMenu = isFilterableField(col) && (getFilterOptionsForColumn(col).length > 0 || (hasCodeValueSource(col) && codeValues && codeValues.isReady));
    
    if (filterObj && 'values' in filterObj && filterObj.values !== undefined) {
      // Normalize values for UI display
      let normalizedValues = filterObj.values;
      
      // If it's the new structured format, convert it back to array format for UI
      if (typeof normalizedValues === 'object' && !Array.isArray(normalizedValues) && 'regularValues' in normalizedValues) {
        const { regularValues, hasBlanks } = normalizedValues;
        normalizedValues = hasBlanks ? ['__BLANK__', ...regularValues] : regularValues;
      }
      
      newFilterValues[columnCode] = normalizedValues;
    } else if (isFacetableField(col) || isFilterableMenu) {
      // Facetable columns should always use arrays
      newFilterValues[columnCode] = [];
    } else if (isBooleanField(col)) {
      newFilterValues[columnCode] = [];
    } else if (isFilterableField(col) && Array.isArray(col.customValues) && col.customValues.length > 0) {
      newFilterValues[columnCode] = [];
    } else if (isSearchableField(col)) {
      newFilterValues[columnCode] = { blank: false, search: '' };
    } else if (isNumberField(col)) {
      newFilterValues[columnCode] = '';
    } else if (isDateField(col)) {
      newFilterValues[columnCode] = { start: null, end: null };
    }
    
    // Set the filter values synchronously
    setPopoverFilterValues(newFilterValues);
    
    // Now open the popover after the state is set
    setFilterAnchor(e.currentTarget);
    setActiveFilterColumn(columnCode);
    setPopoverSearch(""); // Reset search on open
  };

  // On popover close, commit local state to global filter state
  const handlePopoverClose = () => {
    if (!activeFilterColumn) {
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      return;
    }
    const col = columns.find(c => c.columnCode === activeFilterColumn);
    if (!col) {
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      return;
    }
    const localValue = popoverFilterValues[activeFilterColumn];
    let filterString = '';
    let values: any = localValue;

    // --- Prevent unnecessary filter update if value is unchanged ---
    const currentFilterObj = currentStageFilters.find(f => f.fieldConfigurationId === col.fieldConfigurationId);
    let currentValue = currentFilterObj?.values ?? (col.columnTypeCode?.toLowerCase() === 'boolean' ? [] : (col.isSearchable ? { blank: false, search: '' } : (col.isFilterable ? [] : '')));
    
    // Normalize structured format to array format for comparison
    if (typeof currentValue === 'object' && !Array.isArray(currentValue) && 'regularValues' in currentValue) {
      const { regularValues, hasBlanks } = currentValue;
      currentValue = hasBlanks ? ['__BLANK__', ...regularValues] : regularValues;
    }
    
    let normLocal = localValue;
    let normCurrent = currentValue;
    
    // Universal normalization for all column types
    if (isFacetableField(col) || isBooleanField(col)) {
      // For facetable columns: normalize undefined/null to empty array
      normLocal = Array.isArray(localValue) ? localValue : (localValue == null || localValue == undefined ? [] : [localValue]);
      normCurrent = Array.isArray(currentValue) ? currentValue : (currentValue == null || currentValue == undefined ? [] : [currentValue]);
    } else if (isDateField(col)) {
      // For date columns: normalize to {start: null, end: null} format
      normLocal = localValue || { start: null, end: null };
      normCurrent = currentValue || { start: null, end: null };
      // Also handle case where currentValue is an empty array (from facetable logic)
      if (Array.isArray(normCurrent) && normCurrent.length === 0) {
        normCurrent = { start: null, end: null };
      }
    } else if (isSearchableField(col)) {
      // For searchable columns: normalize to { blank: false, search: '' } format
      normLocal = localValue || { blank: false, search: '' };
      normCurrent = currentValue || { blank: false, search: '' };
    } else {
      // For all other columns: normalize to string, treat empty/null as empty string
      normLocal = localValue || [];
      normCurrent = currentValue || [];
    }
    
    const isUnchanged = JSON.stringify(normLocal) === JSON.stringify(normCurrent);

    // --- Facetable logic ---
    if (col.isFacetable) {
      // If nothing is selected, remove the filter for this field only if it exists
      if (!localValue || localValue.length === 0) {
        const stageId = stageConfig?.stageId;
        const hasFilter = !!currentFilterObj;
        if (stageId && hasFilter) {
          setStageFilters(stageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === col.fieldConfigurationId)));
        }
        setPopoverFilterValues(prev => ({ ...prev, [activeFilterColumn]: [] }));
        setFilterAnchor(null);
        setActiveFilterColumn(null);
        return;
      }
      if (isUnchanged) {
        setFilterAnchor(null);
        setActiveFilterColumn(null);
        return;
      }
      // Otherwise, update filter
      const searchColumn = getSearchColumnPath(col);
      const hasBlank = localValue.includes('__BLANK__');
      const regularValues = localValue.filter((val: string) => val !== '__BLANK__');
      
      if (hasBlank) {
        const blankCondition = `${searchColumn} eq null or ${searchColumn} eq ''`;
        if (regularValues.length > 0) {
          const orConditions = regularValues.map((val: string) => `${searchColumn} eq '${val.replace(/'/g, "''")}'`).join(' or ');
          filterString = `(${blankCondition} or ${orConditions})`;
        } else {
          filterString = `(${blankCondition})`;
        }
      } else {
        const orConditions = regularValues.map((val: string) => `${searchColumn} eq '${val.replace(/'/g, "''")}'`).join(' or ');
        filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions;
      }
      updateStageFilter(col.fieldConfigurationId, filterString, { regularValues, hasBlanks: hasBlank });
      setCurrentPage(1); // Reset to first page when adding a new filter
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      setPopoverFilterValues(prev => ({
        ...prev,
        [activeFilterColumn]: localValue
      }));
      return;
    }
    // --- End facetable logic ---

    if (isUnchanged) {
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      return; // Do not update or reload if unchanged
    }

    // Generalized isFacetable logic (including AssignedToUser)
    if (col.isFacetable) {
      const hasBlank = localValue && localValue.includes('__BLANK__');
      const regularValues = localValue ? localValue.filter((val: string) => val !== '__BLANK__') : [];
      if (localValue && localValue.length > 0) {
        const searchColumn = getSearchColumnPath(col);
        const orConditions = regularValues.map((val: string) => `${searchColumn} eq '${val.replace(/'/g, "''")}'`).join(' or ');
        filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions;
      }
      updateStageFilter(col.fieldConfigurationId, filterString, { regularValues, hasBlanks: hasBlank });
      setCurrentPage(1); // Reset to first page when adding a new filter
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      setPopoverFilterValues(prev => ({
        ...prev,
        [activeFilterColumn]: localValue
      }));
      return;
    }
    
    if (col.columnTypeCode?.toLowerCase() === 'boolean') {
      const hasBlank = localValue && localValue.includes('__BLANK__');
      const regularValues = localValue ? localValue.filter((val: string) => val !== '__BLANK__') : [];
      if (localValue && localValue.length > 0) {
        if (hasBlank) {
          const blankCondition = `${col.columnCode} eq null`;
          if (regularValues.length > 0) {
            const orConditions = regularValues.map((val: string) => `${col.columnCode} eq ${val.toLowerCase()}`).join(' or ');
            filterString = `(${blankCondition} or ${orConditions})`;
          } else {
            filterString = blankCondition;
          }
        } else {
          const orConditions = regularValues.map((val: string) => `${col.columnCode} eq ${val.toLowerCase()}`).join(' or ');
          filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions;
        }
      }
      updateStageFilter(col.fieldConfigurationId, filterString, { regularValues, hasBlanks: hasBlank });
      setCurrentPage(1); // Reset to first page when adding a new filter
      setFilterAnchor(null);
      setActiveFilterColumn(null);
      setPopoverFilterValues(prev => ({
        ...prev,
        [activeFilterColumn]: localValue
      }));
      return;
    }
    if (col.isFilterable && ((Array.isArray(col.customValues) && col.customValues.length > 0) || (col.codeValueSource && codeValues && codeValues.isReady))) {
      const hasBlank = localValue && localValue.includes('__BLANK__');
      const regularValues = localValue ? localValue.filter((val: string) => val !== '__BLANK__') : [];
      if (localValue && localValue.length > 0) {
        if (col?.columnTypeCode?.toLowerCase() === 'textlist') {
          const orConditions = regularValues
            .map((val: string) => `x eq '${val.replace(/'/g, "''")}'`)
            .join(' or ');
          if (hasBlank && regularValues.length > 0) {
            // Include empty collections or those matching selected values
            filterString = `(not ${col.columnCode}/any() or ${col.columnCode}/any(x: ${orConditions}))`;
          } else if (hasBlank) {
            // Only empty collections
            filterString = `not ${col.columnCode}/any()`;
          } else if (regularValues.length > 0) {
            // Only selected values
            filterString = `${col.columnCode}/any(x: ${orConditions})`;
          }
        } else {
          const searchColumn = getSearchColumnPath(col);
          const orConditions = regularValues
            .map((val: string) => `${searchColumn} eq '${val.replace(/'/g, "''")}'`)
            .join(' or ');
          if (hasBlank) {
            const blankCondition = `${searchColumn} eq null or ${searchColumn} eq ''`;
            if (regularValues.length > 0) {
              filterString = `(${blankCondition} or ${orConditions})`;
            } else {
              filterString = blankCondition;
            }
          } else {
            filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions;
          }
        }
      }
      updateStageFilter(col.fieldConfigurationId, filterString, { regularValues, hasBlanks: hasBlank });
      setCurrentPage(1); // Reset to first page when adding a new filter
    } else if (col.isSearchable) {
      const searchValue = typeof localValue === 'object' ? localValue : { blank: false, search: '' };
      const hasBlank = searchValue.blank;
      const searchText = (searchValue.search || '').trim();
      
      if (hasBlank || searchText) {
        const searchColumn = getSearchColumnPath(col);
        
        // For TextList fields, use collection empty check
        const blankCondition = col?.columnTypeCode?.toLowerCase() === 'textlist' 
          ? `not ${col.columnCode}/any()`
          : `${searchColumn} eq null or ${searchColumn} eq ''`;
        
        if (hasBlank && searchText) {
          // Both blank and search text: combine with OR - wrap entire expression in parentheses
          const wasSelectedFromSuggestions = selectedFromSuggestions[col.columnCode];
          let searchCondition = '';
          
          if (wasSelectedFromSuggestions) {
            searchCondition = `search.ismatch('${searchText.replace(/'/g, "''")}', '${searchColumn}', 'full', 'all')`;
            setSelectedFromSuggestions(prev => ({ ...prev, [col.columnCode]: false }));
          } else {
            searchCondition = `search.ismatch('/.*${searchText.replace(/'/g, "''")}.*/', '${searchColumn}', 'full', 'any')`;
          }
          
          filterString = `((${blankCondition}) or (${searchCondition}))`;
        } else if (hasBlank) {
          // Only blank
          filterString = `(${blankCondition})`;
        } else if (searchText) {
          // Only search text
          const wasSelectedFromSuggestions = selectedFromSuggestions[col.columnCode];
          
          if (wasSelectedFromSuggestions) {
            filterString = `search.ismatch('${searchText.replace(/'/g, "''")}', '${searchColumn}', 'full', 'all')`;
            setSelectedFromSuggestions(prev => ({ ...prev, [col.columnCode]: false }));
          } else {
            filterString = `search.ismatch('/.*${searchText.replace(/'/g, "''")}.*/', '${searchColumn}', 'full', 'any')`;
          }
        }
        updateStageFilter(col.fieldConfigurationId, filterString, searchValue);
        setCurrentPage(1); // Reset to first page when adding a new filter
      } else {
        // If both blank and search are empty, remove the filter
        const stageId = stageConfig?.stageId;
        if (stageId) {
          setStageFilters(stageFilters.filter(f => !(f.stageId === stageId && f.fieldConfigurationId === col.fieldConfigurationId)));
        }
      }
    } else if (isNumberField(col)) {
      const value = (localValue || '').trim();
      if (value) {
        filterString = `${col.columnCode} eq ${value}`;
      }
      updateStageFilter(col.fieldConfigurationId, filterString, value);
      setCurrentPage(1); // Reset to first page when adding a new filter
    } else if (isDateField(col)) {
      const { start, end } = localValue || {};
      if (start && !end) {
        const startStr = `${start}T00:00:00Z`;
        const nextDay = new Date(Date.UTC(parseInt(start.split('-')[0]), parseInt(start.split('-')[1]) - 1, parseInt(start.split('-')[2]) + 1));
        const nextDayStr = `${nextDay.toISOString().split('T')[0]}T00:00:00Z`;
        filterString = `${col.columnCode} ge ${startStr} and ${col.columnCode} lt ${nextDayStr}`;
      } else if (start && end) {
        const startStr = `${start}T00:00:00Z`;
        const nextDay = new Date(Date.UTC(parseInt(end.split('-')[0]), parseInt(end.split('-')[1]) - 1, parseInt(end.split('-')[2]) + 1));
        const nextDayStr = `${nextDay.toISOString().split('T')[0]}T00:00:00Z`;
        filterString = `${col.columnCode} ge ${startStr} and ${col.columnCode} lt ${nextDayStr}`;
      }
      updateStageFilter(col.fieldConfigurationId, filterString, localValue);
      setCurrentPage(1); // Reset to first page when adding a new filter
      setDateFilter(localValue || { start: null, end: null });
    }
    setFilterAnchor(null);
    setActiveFilterColumn(null);
    setPopoverFilterValues(prev => ({
      ...prev,
      [activeFilterColumn]: localValue
    }));
  };

  // Update Clear Filters handler to clear both stage and global filters and reset globalFilterSelections
  function handleClearAllFilters() {
    // Track clear all filters action
    trackFilterAction('clear', 'all_filters', { stageFilters: currentStageFilters.length, globalFilters: globalFilters.length });
    
    const stageId = stageConfig?.stageId;
    if (stageId) {
      // Only clear filters for the current stage
      setStageFilters(stageFilters.filter(f => f.stageId !== stageId));
    }
    setGlobalFilters([]);
    setGlobalFilterSelections({}); // Reset sidebar/global filter count state
    setSortState({ columnCode: null, direction: null });
    // --- Clear popoverFilterValues for all facetable columns ---
    const facetableColumns = columns.filter(c => c.isFacetable);
    setPopoverFilterValues(prev => {
      const cleared = { ...prev };
      facetableColumns.forEach(col => { cleared[col.columnCode] = []; });
      return cleared;
    });
    // --- End clear ---
    setCurrentPage(1); // Reset to first page after clearing filters
  }

  // Sorting click handler
  const handleSortClick = (columnCode: string) => {
    // Track sort action
    trackGridAction('sort', { columnCode, currentDirection: sortState.direction });
    
    setSortState(prev => {
      if (prev.columnCode !== columnCode) {
        return { columnCode, direction: 'asc' };
      } else if (prev.direction === 'asc') {
        return { columnCode, direction: 'desc' };
      } else if (prev.direction === 'desc') {
        return { columnCode: null, direction: null };
      } else {
        return { columnCode, direction: 'asc' };
      }
    });
  };

  // --- Pending items toggle logic ---
  // Locate status column for current stage
  const statusColumn = useMemo(
    () => columns.find((c: any) => c.columnCode === 'WorkOrderVersionStatusCode'),
    [columns]
  );

  // Check if the pending filter is currently active for this stage
  const isPendingActive = useMemo(() => {
    if (!statusColumn) return false;
    const existing = currentStageFilters.find(
      (f: any) => f.fieldConfigurationId === statusColumn.fieldConfigurationId
    );
    if (!existing) return false;
    if (Array.isArray(existing.values)) {
      const selected = existing.values as string[];
      return (
        selected.length === pendingStatusValues.length &&
        pendingStatusValues.every((v) => selected.includes(v))
      );
    }
    return false;
  }, [currentStageFilters, statusColumn, pendingStatusValues]);

  // Apply or clear the pending filter
  const setPendingFilter = (enabled: boolean) => {
    if (!statusColumn || !stageConfig?.stageId) return;
    if (enabled) {
      const searchColumn = getSearchColumnPath(statusColumn);
      const orConditions = pendingStatusValues
        .map((v) => `${searchColumn} eq '${v.replace(/'/g, "''")}'`)
        .join(' or ');
      const filterString = `(${orConditions})`;
      updateStageFilter(statusColumn.fieldConfigurationId, filterString, { regularValues: pendingStatusValues, hasBlanks: false });
      setCurrentPage(1);
    } else {
      setStageFilters(
        stageFilters.filter(
          (f: any) =>
            !(f.stageId === stageConfig.stageId && f.fieldConfigurationId === statusColumn.fieldConfigurationId)
        )
      );
      setCurrentPage(1);
    }
  };

  // Add this handler function inside WorkflowGrid:
  function handleClearColumnFilter(column: any) {
    // Track clear column filter action
    trackFilterAction('clear', 'column_filter', column.columnCode);
    
    updateFilter(column.columnCode, []);
    const stageId = stageConfig?.stageId;
    if (stageId) {
      setStageFilters(stageFilters.filter((f: { stageId: string; fieldConfigurationId: string, filter: string }) => 
        !(f.stageId === stageId && f.fieldConfigurationId === column.fieldConfigurationId)
      ));
    }
    setPopoverFilterValues(prev => ({ 
      ...prev, 
      [column.columnCode]: isDateField(column) 
        ? { start: null, end: null } 
        : isSearchableField(column)
          ? { blank: false, search: '' }
          : Array.isArray(prev[column.columnCode]) 
            ? [] 
            : '' 
    }));
    setSelectedFromSuggestions(prev => ({ ...prev, [column.columnCode]: false }));
    // Clear the draft ref for searchable fields
    if (isSearchableField(column)) {
      searchableDraftRef.current[column.columnCode] = '';
    }
    setFilterAnchor(null);
    setActiveFilterColumn(null);
  }


  useEffect(() => {
    if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight || 0);
    if (paginationRef.current) setPaginationHeight(paginationRef.current.offsetHeight || 0);
  }, [columns]);

  // Update fetchGlobalFacets to use the ref
  async function fetchGlobalFacets({ search, facetKeys }: { search: string, facetKeys: string[] }) {
    if (!facetKeys || facetKeys.length === 0) return;
    if (fetchingFacets.current) return; // Prevent concurrent fetches
    fetchingFacets.current = true;
    try {
      // Use column codes for SELECT (to get full objects), facetKeys for facets (to aggregate by sub-properties)
      const selectFields = getSelectFieldsFromConfiguration(configuration);
      const select = selectFields.join(',');
      let filter = `(Stage ne '' and Stage ne null and Stage ne 'Discarded' and IsDeleted eq false)`;
      const response = await searchWorkflowItems(
        undefined,
        undefined,
        [],
        1,
        0,
        search,
        select,
        filter,
        undefined,
        facetKeys
      );
      setFacetsFetched(true); // Only set after successful fetch
    } catch (err) {
      console.error('Failed to fetch global facets', err);
    } finally {
      fetchingFacets.current = false;
    }
  }


  // Update sidebar open effect: fetch facets if missing when sidebar is opened, and only if not already fetched
  useEffect(() => {
    if (isGlobalFilterSidebarOpen) {
      if (
        !facetsFetched &&
        Array.isArray(configuration?.globalFilterIds) &&
        configuration.globalFilterIds.length > 0 &&
        assignedUsers && assignedUsers.length > 0
      ) {
        const globalFilterFields = configuration.fieldsInUse.filter(f => Array.isArray(configuration.globalFilterIds) && configuration.globalFilterIds.includes(f.fieldConfigurationId));
        const facetKeys = globalFilterFields.map(f => getSearchColumnPath(f));
        fetchGlobalFacets({ search: '*', facetKeys });
      }
      // Sync globalFilterSelections with globalFilters
      if (Array.isArray(globalFilters)) {
        const selections: Record<string, string[]> = {};
        globalFilters.forEach(f => {
          const field = configuration?.fieldsInUse.find(field => field.fieldConfigurationId === f.fieldConfigurationId);
          if (!field) return;
          
          // Try to use metadata first (for new filters with special flags)
          if (f.values) {
            try {
              const metadata = JSON.parse(f.values);
              const combinedValues = [];
              
              if (metadata.isSelectAll) {
                combinedValues.push('__SELECT_ALL__');
              } else {
                if (metadata.hasBlanks) {
                  combinedValues.push('__BLANK__');
                }
                if (metadata.regularValues && Array.isArray(metadata.regularValues)) {
                  combinedValues.push(...metadata.regularValues);
                }
              }
              
              if (combinedValues.length > 0) {
                selections[f.fieldConfigurationId] = combinedValues;
              }
              return;
            } catch (error) {
              console.warn('Failed to parse filter metadata, falling back to regex parsing:', error);
            }
          }
          
          // Fallback to regex parsing for legacy filters
          const searchColumn = getSearchColumnPath(field);
          const filter = f.filter;
          
          // Check for Select All pattern: "ColumnName ne null and ColumnName ne ''"
          const selectAllPattern = new RegExp(`${searchColumn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} ne null and ${searchColumn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} ne ''`);
          if (selectAllPattern.test(filter)) {
            selections[f.fieldConfigurationId] = ['__SELECT_ALL__'];
            return;
          }
          
          // Check for Blank pattern: "ColumnName eq null or ColumnName eq ''"
          const blankPattern = new RegExp(`${searchColumn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} eq null or ${searchColumn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} eq ''`);
          const hasBlank = blankPattern.test(filter);
          
          // Extract regular values from the filter
          const regularMatches = Array.from(filter.matchAll(/eq '([^']+)'/g));
          const regularValues = regularMatches.map(m => m[1]);
          
          // Combine blank and regular values
          const combinedValues = [];
          if (hasBlank) {
            combinedValues.push('__BLANK__');
          }
          combinedValues.push(...regularValues);
          
          if (combinedValues.length > 0) {
            selections[f.fieldConfigurationId] = combinedValues;
          }
        });
        setGlobalFilterSelections(selections);
      }
    }
  }, [isGlobalFilterSidebarOpen, facetsFetched, configuration, assignedUsers, globalFilters]);

  // Restore drawerVisible effect
  useEffect(() => {
    if (isGlobalFilterSidebarOpen) {
      setDrawerVisible(true);
    } else if (drawerVisible) {
      const timeout = setTimeout(() => setDrawerVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isGlobalFilterSidebarOpen]);

  // Handler for show more
  function handleShowMore(fieldId: string, total: number) {
    // Track show more action
    trackFilterAction('modify', 'show_more_options', { fieldId, currentCount: globalFilterShowCount[fieldId] || 3, total });
    
    setGlobalFilterShowCount(prev => ({
      ...prev,
      [fieldId]: Math.min((prev[fieldId] || 3) + 10, total)
    }));
  }

  // Handler for checkbox change
  function handleGlobalCheckboxChange(fieldId: string, value: string) {
    setGlobalFilterSelections(prev => {
      const prevSelected = prev[fieldId] || [];
      
      // Handle special values
      if (value === '__SELECT_ALL__') {
        // Select all: get all available options for this field
        const field = configuration?.fieldsInUse.find(f => f.fieldConfigurationId === fieldId);
        if (field) {
          const facetOptions = getFacetOptionsForColumnSidebar(facets, field);
          const allOptionValues = [
            ...facetOptions.map((opt: { value: string }) => opt.value),
            '__BLANK__'
          ];
          const isCurrentlySelected = prevSelected.length === allOptionValues.length && 
                                   allOptionValues.every(val => prevSelected.includes(val));
          // If Select All is currently selected, unselect all items
          // If Select All is not selected, select all items
          return { ...prev, [fieldId]: isCurrentlySelected ? [] : allOptionValues };
        }
        return { ...prev, [fieldId]: [] };
      } else if (value === '__BLANK__') {
        // (blank): can be combined with other selections
        const next = prevSelected.includes(value)
          ? prevSelected.filter(v => v !== value)
          : [...prevSelected, value];
        return { ...prev, [fieldId]: next };
      } else {
        // Regular value: can be combined with other selections
        const next = prevSelected.includes(value)
          ? prevSelected.filter(v => v !== value)
          : [...prevSelected, value];
        return { ...prev, [fieldId]: next };
      }
    });
  }

  // Handler for Apply
  function handleGlobalApply() {
    // Track global filter apply action
    trackFilterAction('apply', 'global_filters', { filterCount: Object.keys(globalFilterSelections).length });
    
    const newGlobalFilters = Object.entries(globalFilterSelections)
      .filter(([_, values]) => values.length > 0)
      .map(([fieldId, values]) => {
        const field = configuration?.fieldsInUse.find(f => f.fieldConfigurationId === fieldId);
        if (!field) return null;
        let filterString = '';
        const searchColumn = getSearchColumnPath(field);
        
        // Handle special cases
        const hasSelectAll = values.includes('__SELECT_ALL__');
        const hasBlank = values.includes('__BLANK__');
        const regularValues = values.filter(v => v !== '__SELECT_ALL__' && v !== '__BLANK__');
        
        if (hasSelectAll) {
          // Select all: any non-empty value
          if (field.columnTypeCode?.toLowerCase() === 'textlist') {
            filterString = `${searchColumn}/any()`;
          } else {
            filterString = `${searchColumn} ne null and ${searchColumn} ne ''`;
          }
        } else if (hasBlank && regularValues.length > 0) {
          // (blank) combined with regular values
          if (field.columnTypeCode?.toLowerCase() === 'textlist') {
            const orConditions = regularValues.map(val => `x eq '${val.replace(/'/g, "''")}'`).join(' or ');
            filterString = `(not ${searchColumn}/any() or ${searchColumn}/any(x: ${orConditions}))`;
          } else {
            const blankCondition = `${searchColumn} eq null or ${searchColumn} eq ''`;
            const regularConditions = regularValues.map(val => `${searchColumn} eq '${val.replace(/'/g, "''")}'`).join(' or ');
            filterString = `(${blankCondition}) or (${regularConditions})`;
          }
        } else if (hasBlank) {
          // Only (blank)
          if (field.columnTypeCode?.toLowerCase() === 'textlist') {
            filterString = `not ${searchColumn}/any()`;
          } else {
            filterString = `${searchColumn} eq null or ${searchColumn} eq ''`;
          }
        } else if (regularValues.length > 0) {
          // Regular values only
          if (field.columnTypeCode?.toLowerCase() === 'boolean') {
            const orConditions = regularValues.map(val => `${searchColumn} eq ${val.toLowerCase()}`).join(' or ');
            filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions;
          } else if (field.columnTypeCode?.toLowerCase() === 'textlist') {
            const orConditions = regularValues.map(val => `x eq '${val.replace(/'/g, "''")}'`).join(' or ');
            filterString = `${searchColumn}/any(x: ${orConditions})`;
          } else {
            const orConditions = regularValues.map(val => `${searchColumn} eq '${val.replace(/'/g, "''")}'`).join(' or ');
            filterString = regularValues.length > 1 ? `(${orConditions})` : orConditions;
          }
        }
        
        if (filterString) {
          filterString = `(Stage ne 'Discarded' and IsDeleted eq false) and (${filterString})`;
        }
        
        // Create metadata for reapplication
        const metadata = {
          isSelectAll: hasSelectAll,
          hasBlanks: hasBlank,
          regularValues: regularValues
        };
        
        return { 
          fieldConfigurationId: fieldId, 
          filter: filterString,
          values: JSON.stringify(metadata)
        };
      })
              .filter(Boolean) as { fieldConfigurationId: string, filter: string, values?: string }[];
    // --- Prevent unnecessary global filter update if unchanged ---
    const isUnchanged = JSON.stringify(newGlobalFilters) === JSON.stringify(globalFilters);
    if (isUnchanged) {
      setIsGlobalFilterSidebarOpen(false);
      setGlobalFilterShowCount({});
      return; // Do not update or reload if unchanged
    }
    // --- End check ---
    setGlobalFilters(newGlobalFilters);
    setCurrentPage(1); // Reset to first page when applying global filters
    setIsGlobalFilterSidebarOpen(false);
    setGlobalFilterShowCount({});
  }

  // Handler for Clear all
  function handleGlobalClearAll() {
    // Track global filter clear action
    trackFilterAction('clear', 'global_filters', { filterCount: Object.keys(globalFilterSelections).length });
    
    setGlobalFilterSelections({});
    setGlobalFilters([]);
    setGlobalFilterShowCount({});
  }

  // Helper to get filtered global filter fields and options (use globalFacets)
  function getFilteredGlobalFilterFields(): { field: any; options: { label: string; value: string; count: number }[] }[] {
    if (!Array.isArray(configuration?.globalFilterIds) || configuration.globalFilterIds.length === 0) return [];
    const search = globalFilterSearch.trim().toLowerCase();
    return configuration.globalFilterIds
      .map(fid => {
        const field = configuration.fieldsInUse.find(f => f.fieldConfigurationId === fid);
        if (!field) return undefined;
        const facetOptions = getFacetOptionsForColumn(facets, field);
        if (!facetOptions || facetOptions.length === 0) return undefined;
        // Map options with display label
        const options = facetOptions.map((opt: { value: string; count: number }) => {
          let label = getOptionLabel(opt, field, sortedAssignedUsers, language);
          return { ...opt, label };
        });
        // If searching, filter options by label or value
        const filteredOptions = search
          ? options.filter(opt => {
              const matchesLabel = opt.label.toLowerCase().includes(search);
              const matchesValue = opt.value && opt.value.toLowerCase().includes(search);
              
              // For AssignedToUser, only search by label (display name), not email
              const shouldInclude = field.columnCode === 'AssignedToUser' 
                ? matchesLabel 
                : (matchesLabel || matchesValue);
              
              return shouldInclude;
            })
          : options;
        
        // Always include the field if it has selected special values, even if no regular options match the search
        const selected = globalFilterSelections[fid] || [];
        const hasSpecialSelections = selected.includes('__SELECT_ALL__') || selected.includes('__BLANK__');
        
        if (filteredOptions.length === 0 && !hasSpecialSelections) return undefined;
        return { field, options: filteredOptions };
      })
      .filter((x): x is { field: any; options: { label: string; value: string; count: number }[] } => !!x);
  }

  // Use context-provided stageFilters, globalFilters, setStageFilters, setGlobalFilters, clearAllFilters
  // Remove any local state for stageFilters/globalFilters and their updaters
  // Remove or replace any references to setGlobalFilters(prev => ...) with setGlobalFilters([...]) or similar, using the correct type
  // For selectedSectionCount, count the number of globalFilters with a non-empty filter array
  const selectedSectionCount = Object.values(globalFilterSelections).filter(arr => arr && arr.length > 0).length;

  // Helper to get facet options for a column, now using globalFacets for sidebar
  function getFacetOptionsForColumnSidebar(facets: any, column: any): { value: string; count: number }[] {
    if (!facets || !column) return [];
    
    const searchPath = getSearchColumnPath(column);
    
    // 1. Direct match with search path
    if (Array.isArray(facets[searchPath]) && facets[searchPath].length > 0) {
      return facets[searchPath].filter((opt: { value: string; count: number }) => opt.value !== "");
    }
    // 2. Direct match with column code
    if (Array.isArray(facets[column.columnCode]) && facets[column.columnCode].length > 0) {
      return facets[column.columnCode].filter((opt: { value: string; count: number }) => opt.value !== "");
    }
    // 3. Case-insensitive match
    const foundKey = Object.keys(facets).find(
      k => k.toLowerCase() === searchPath.toLowerCase()
    );
    if (foundKey && Array.isArray(facets[foundKey])) {
      return facets[foundKey].filter((opt: { value: string; count: number }) => opt.value !== "");
    }
    // 4. Try replacing / with nothing (for edge cases)
    const altKey = searchPath.replace(/\//g, '');
    const foundAlt = Object.keys(facets).find(
      k => k.replace(/\//g, '').toLowerCase() === altKey.toLowerCase()
    );
    if (foundAlt && Array.isArray(facets[foundAlt])) {
      return facets[foundAlt].filter((opt: { value: string; count: number }) => opt.value !== "");
    }
    return [];
  }

  // Update filter button count to use globalFilters (persistent across stages)
  const globalFilterCount = globalFilters.length;

  // Use workOrdersFacets from useWorkflow() context directly
  const stageFacets = useMemo(() => workOrdersFacets || {}, [workOrdersFacets]);

  // Sort assignedUsers by fullName (A-Z, case-insensitive)
  const sortedAssignedUsers = Array.isArray(assignedUsers)
    ? [...assignedUsers].sort((a, b) =>
        (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase())
      )
    : [];

  const getFilterOptionsForColumn = (column: any) => {
    const isBoolean = column.columnTypeCode?.toLowerCase() === 'boolean';
    const hasCustomValues = Array.isArray(column.customValues) && column.customValues.length > 0;
    const hasCodeValueSource = column.codeValueSource && codeValues && codeValues.isReady;

    // If column has CodeValueSource, use code values from the hook
    if (hasCodeValueSource) {
      try {
        const codeValuesFromHook = codeValues.getCodeValues(column.codeValueSource, false);
        if (codeValuesFromHook && Array.isArray(codeValuesFromHook)) {
          return codeValuesFromHook
            .filter((item: any) => 
              item && 
              item.code && 
              item.code.trim() !== '' && 
              item.text && 
              item.text.trim() !== ''
            )
            .map((item: any) => ({
              code: item.code,
              titleEnglish: item.text,
              titleFrench: item.text,
              color: item.Icon || '',
              isDeleted: false
            }));
        }
      } catch (error) {
        console.warn('Failed to get code values from hook:', error);
      }
    }

    // Otherwise use existing logic
    if (isBoolean) {
      return hasCustomValues ? column.customValues : defaultBooleanOptions;
    }
    return hasCustomValues ? column.customValues : [];
  };

  // Add cleanup for debounce timeouts
  useEffect(() => {
    return () => {
      // Clear all debounce timeouts on unmount
      Object.values(suggestionsDebounceTimeout.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Message sidebar state is now managed by MessageTrigger component callbacks

  // Component for searchable fields with suggestions
  const SearchableFieldWithSuggestions = ({ column }: { column: any }) => {
    const currentValue = popoverFilterValues[column.columnCode] || { blank: false, search: '' };
    const currentSearchText = typeof currentValue === 'object' ? (currentValue.search || '') : (typeof currentValue === 'string' ? currentValue : '');
    const currentBlankState = typeof currentValue === 'object' ? (currentValue.blank || false) : false;
    
    // Use ref-based draft to prevent parent re-render on every keystroke
    const initialDraft = searchableDraftRef.current[column.columnCode] ?? currentSearchText;
    const [inputValue, setInputValue] = useState(initialDraft);
    const [isBlankChecked, setIsBlankChecked] = useState(currentBlankState);
    
    const suggestionsList = suggestions[column.columnCode] || [];
    const isLoading = isLoadingSuggestions[column.columnCode] || false;

    // Check if this field should have suggestions
    const shouldShowSuggestions = [
      "WorkerFullName",
      "WorkerEmail", 
      "RecruiterFullName",
      "RecruiterEmail",
      "ManagerFullName",
      "ManagerEmail",
      "PrimaryJobOwnerFullName",
      "PrimaryJobOwnerEmail"
    ].includes(column.columnCode);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      // Keep drafts in a ref to prevent parent re-render/caret jump
      searchableDraftRef.current[column.columnCode] = value;
      if (shouldShowSuggestions) {
        debouncedSearch(column.columnCode, value);
      }
    };

    const handleBlankChange = (checked: boolean) => {
      setIsBlankChecked(checked);
    };

    const handleSuggestionClick = (suggestion: any) => {
      const value = typeof suggestion === 'string' ? suggestion : (suggestion.text || suggestion);
      
      // Track suggestion selection
      trackFilterAction('apply', 'suggestion_selection', { columnCode: column.columnCode, suggestionValue: value });
      
      setSelectedFromSuggestions(prev => ({ ...prev, [column.columnCode]: true }));
      
      // Clear the draft ref since we're applying the filter
      searchableDraftRef.current[column.columnCode] = value;
      
      // Apply the filter immediately with the selected value
      const searchColumn = getSearchColumnPath(column);
      
      // For TextList fields, use collection empty check
      const blankCondition = column?.columnTypeCode?.toLowerCase() === 'textlist' 
        ? `not ${column.columnCode}/any()`
        : `${searchColumn} eq null or ${searchColumn} eq ''`;
      
      let filterString = '';
      if (isBlankChecked) {
        // Combine blank with search - wrap entire expression in parentheses for proper grouping
        const searchCondition = `search.ismatch('${value.replace(/'/g, "''")}', '${searchColumn}', 'full', 'all')`;
        filterString = `((${blankCondition}) or (${searchCondition}))`;
      } else {
        filterString = `search.ismatch('${value.replace(/'/g, "''")}', '${searchColumn}', 'full', 'all')`;
      }
      
      updateStageFilter(column.fieldConfigurationId, filterString, { blank: isBlankChecked, search: value });
      setCurrentPage(1);
      
      // Close the popover
      setFilterAnchor(null);
      setActiveFilterColumn(null);
    };

    // Handle manual search (similar to suggestions but without selectedFromSuggestions flag)
    const handleManualSearch = () => {
      const value = inputValue.trim();
      if (!value && !isBlankChecked) {
        // If both are empty, clear the filter
        handleClearColumnFilter(column);
        return;
      }
      
      // Update the draft ref with the current value
      searchableDraftRef.current[column.columnCode] = value;
      
      // Apply the filter immediately - BYPASS handlePopoverClose()
      const searchColumn = getSearchColumnPath(column);
      
      // For TextList fields, use collection empty check
      const blankCondition = column?.columnTypeCode?.toLowerCase() === 'textlist' 
        ? `not ${column.columnCode}/any()`
        : `${searchColumn} eq null or ${searchColumn} eq ''`;
      
      let filterString = '';
      if (isBlankChecked && value) {
        // Both blank and search - wrap entire expression in parentheses for proper grouping
        const searchCondition = `search.ismatch('/.*${value.replace(/'/g, "''")}.*/', '${searchColumn}', 'full', 'any')`;
        filterString = `((${blankCondition}) or (${searchCondition}))`;
      } else if (isBlankChecked) {
        // Only blank
        filterString = `(${blankCondition})`;
      } else if (value) {
        // Only search
        filterString = `search.ismatch('/.*${value.replace(/'/g, "''")}.*/', '${searchColumn}', 'full', 'any')`;
      }
      
      updateStageFilter(column.fieldConfigurationId, filterString, { blank: isBlankChecked, search: value });
      setCurrentPage(1);
      
      // Close the popover
      setFilterAnchor(null);
      setActiveFilterColumn(null);
    };


    return (
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={e => { 
            if (e.key === 'Enter') {
              handleManualSearch();
            }
          }}
          placeholder={`${t('typeToFilter')}`}
          onClick={e => e.stopPropagation()}
          className="focus:border-gray-300 focus:outline-none"
        />
        
        {/* (blank) checkbox option */}
        <label 
          className="flex items-center gap-2 cursor-pointer mt-3" 
          onClick={e => e.stopPropagation()}
        >
          <Checkbox
            checked={isBlankChecked}
            onCheckedChange={handleBlankChange}
          />
          <span style={{ fontStyle: 'italic', color: '#666' }}>{t('blank')}</span>
        </label>
        
        {/* Inline suggestions - similar to other filter types */}
        {shouldShowSuggestions && inputValue.trim().length >= 2 && (
          <div className="mt-3 space-y-2">
            {isLoading ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {t('loading')}...
              </div>
            ) : suggestionsList.length > 0 ? (
              <div className="space-y-0.5">
                <div className="text-xs text-gray-500 font-medium mb-1">Suggestions:</div>
                {suggestionsList.slice(0, 5).map((suggestion, index) => (
                  <button
                    key={index}
                    className="w-full text-left px-2 py-1 text-sm hover:bg-blue-50 rounded transition-colors"
                    onClick={() => handleSuggestionClick(suggestion)}
                    type="button"
                  >
                    {typeof suggestion === 'string' ? suggestion : (suggestion.text || suggestion)}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {t('noSuggestions')}
              </div>
            )}
          </div>
        )}
        
        <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={e => {
              e.stopPropagation();
              // Always call handleManualSearch - it will handle clearing if both are empty
              handleManualSearch();
            }} style={{ flex: 1 }}>
              {t('apply')}
            </Button>
            <Button variant="destructive" size="sm" onClick={e => {
              e.stopPropagation();
              handleClearColumnFilter(column);
            }} style={{ flex: 1 }}>
              {t('clearAll')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode)
  const hasManageViewsPermission = authorizedActions.includes("ManageViews")
  const canEditFieldData = authorizedActions.includes("EditFieldData")
  const canViewNavigationLinks = authorizedActions.includes("ViewNavigationLinks")

  return (
    <div className="flex flex-col h-full overflow-hidden"
      style={{
        transition: 'margin-right 0.3s cubic-bezier(0.4,0,0.2,1)',
        marginRight: isGlobalFilterSidebarOpen ? FILTER_DRAWER_WIDTH : 0,
      }}
    >
      <GridHeader
        ref={headerRef}
        stage={stage as WorkflowStage}
        stageConfig={stageConfig}
        language={language}
        totalRecords={totalCount}
        selectedCount={selectedItems.length}
        hasActiveFilters={hasActiveFilters}
        onEditColumns={() => setIsEditColumnsOpen(true)}
        onToggleFilter={() => setIsGlobalFilterSidebarOpen(true)}
        filterCount={globalFilterCount}
        isFilterActive={isGlobalFilterSidebarOpen}
        onClearFilters={handleClearAllFilters}
        onDeselectAll={deselectAllItems}
        showFilterButton={true}
        isLoadingHeader={(isLoadingWorkOrders && !isJustPageChange.current) || isLoadingConfiguration || !isHeightCalculated}
        onBulkEdit={() => setIsBulkEditOpen(true)}
        onMoveTo={() => setIsMoveToOpen(true)}
        showPinDock={true}
        onPinDockDrop={() => {
          // Drop to dock = unpin for current stage
          const currentStageConfig = stages.find(s => s.stageCode.toLowerCase() === (stage || '').toLowerCase());
          if (!currentStageConfig) return;
          setPinnedColumn(null);
        }}
        onPinDockClick={() => {
          // If no pinned column, clicking dock does nothing for now
        }}
        showEditColumnsButton={hasManageViewsPermission}
      />

      {/* Pending items toggle */}
      {configuration?.showPendingStatusQuickFilter && (
        <div className="flex items-center gap-2 mb-2" style={{ paddingLeft: 2, marginBottom: 20 }}>
          <Switch
            checked={isPendingActive}
            onCheckedChange={(checked) => setPendingFilter(Boolean(checked))}
            aria-label={t('showChangesInProgress')}
            className="data-[state=checked]:bg-[#0F214B] focus-visible:ring-[#0F214B]"
          />
          <span className="text-sm text-gray-700">{t('showChangesInProgress')}</span>
        </div>
      )}

      <div ref={mainContainerRef} className="overflow-hidden flex flex-col border rounded-md flex-1" style={{ position: 'relative', background: '#fff', maxHeight: 'calc(100vh)' }}>
        <div ref={tableContainerRef} style={{ width: '100%', position: 'relative', flex: 1, minWidth: '100%', overflow: isMessageSidebarOpen ? 'unset' : 'auto' }}>
          <Table
            className="w-full resizable-table workflow-grid"
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              tableLayout: 'fixed',
              borderCollapse: 'separate',
              borderSpacing: 0,
              userSelect: isResizing ? 'none' : 'auto',
              width: (() => {
                // Calculate total width: checkbox (50) + all dynamic columns
                const checkboxWidth = 50;
                const dataColumnsWidth = displayColumns.reduce((total, column) => {
                  return total + getSafeColumnWidth(columnWidths, column.fieldConfigurationId);
                }, 0);
                return `${checkboxWidth + dataColumnsWidth}px`;
              })(),
              minWidth: (() => {
                // Same calculation for minWidth
                const checkboxWidth = 50;
                const dataColumnsWidth = displayColumns.reduce((total, column) => {
                  return total + getSafeColumnWidth(columnWidths, column.fieldConfigurationId);
                }, 0);
                return `${checkboxWidth + dataColumnsWidth}px`;
              })(),
            }}
            ref={(tableRef) => {
              if (tableRef && isResizing) {
                // Force table layout
                if (tableRef.style) {
                  tableRef.style.tableLayout = 'fixed';
                  // Recalculate width during resize
                  const checkboxWidth = 50;
                  const dataColumnsWidth = displayColumns.reduce((total, column) => {
                    return total + (getSafeColumnWidth(columnWidths, column.fieldConfigurationId));
                  }, 0);
                  tableRef.style.width = `${checkboxWidth + dataColumnsWidth}px`;
                }
              }
            }}
          >
            <Header className="sticky top-0" style={{ background: '#F9F9FF', position: 'sticky', top: 0, zIndex: isMessageSidebarOpen ? Z_INDEX.GRID_HEADER_MESSAGE_SIDEBAR_OPEN : Z_INDEX.GRID_HEADER }}>
              <TableRow style={{ background: '#F9F9FF', borderBottom: '1px solid #ECECEC' }}>
                {/* Checkbox column header */}
                <TableHead 
                  className="border-b border-r align-middle whitespace-nowrap font-medium group" 
                  style={{
                    background: isMessageSidebarOpen ? 'rgba(249, 249, 255, 0.6)' : '#F9F9FF', 
                    padding: '8px 12px',
                    width: 50,
                    minWidth: 50,
                    maxWidth: 50,
                    position: 'sticky',
                    left: 0,
                    zIndex: isMessageSidebarOpen ? Z_INDEX.FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN : Z_INDEX.FIXED_COLUMN
                  }}
                >
                  <div className="flex items-center justify-center">
                    <Checkbox
                      checked={isAllSelected ? true : selectedItems.length > 0 ? 'indeterminate' : false}
                      onCheckedChange={() => {
                        if (isAllSelected) {
                          deselectAllItems();
                        } else {
                          selectAllItems();
                        }
                      }}
                      aria-label="Select all rows"
                    />
                  </div>

                </TableHead>
                
                {/* Render all dynamic columns */}
                {displayColumns.map((column, index) => {
                  const showFilterIcon = !!(column.isFacetable || column.isFilterable || column.isSearchable);
                  const isSortable = column.isSortable !== false;
                  const filterOptions = getFilterOptionsForColumn(column);
                  const isFilterableMenu = column.isFilterable && (filterOptions.length > 0 || (column.codeValueSource && codeValues && codeValues.isReady));
                  // Use new helper to determine if this column is filtered for the current stage
                  const filtered = isColumnFiltered(currentStageFilters, column);
                  const columnWidth = getSafeColumnWidth(columnWidths, column.fieldConfigurationId);
                  const currentStageConfig = stages.find(s => s.stageCode.toLowerCase() === (stage || '').toLowerCase());
                  const pinnedMap = state.pinnedColumnsByStage || {};
                  const pinnedId = currentStageConfig ? pinnedMap[currentStageConfig.stageId] : undefined;
                  const isSticky = pinnedId ? (displayColumns.findIndex(c => c.fieldConfigurationId === column.fieldConfigurationId) <= displayColumns.findIndex(c => c.fieldConfigurationId === pinnedId)) : false;
                  const isPinned = pinnedId === column.fieldConfigurationId;
                  // Calculate left offset including checkbox width and all previous column widths
                  const checkboxWidth = 50;
                  let leftOffset = checkboxWidth;
                  if (isSticky) {
                    const currentIndex = displayColumns.findIndex(c => c.fieldConfigurationId === column.fieldConfigurationId);
                    for (let i = 0; i < currentIndex; i++) {
                      leftOffset += getSafeColumnWidth(columnWidths, displayColumns[i].fieldConfigurationId);
                    }
                  }
                  return (
                    <TableHead
                      key={column.fieldConfigurationId}
                      className="border-b border-r align-middle whitespace-nowrap font-medium group"
                      style={{
                         background: column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen ? 'rgba(249, 249, 255, 0.6)' : '#F9F9FF',
                        color: '#222',
                        fontWeight: 500,
                        fontSize: 14,
                        padding: '8px 12px',
                        textAlign: 'center',
                        borderBottom: '1px solid #ECECEC',
                        borderRight: '1px solid #ECECEC',
                        borderTopRightRadius: index === columns.length - 1 ? 16 : undefined,
                        position: isSticky ? 'sticky' : 'relative',
                        left: isSticky ? leftOffset : undefined,
                        zIndex: isSticky ? Z_INDEX.STICKY_COLUMN : (column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen ? Z_INDEX.FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN : (column.isActionColumn && column.actionType === 'message' ? (Z_INDEX.GRID_HEADER-1) : (isMessageSidebarOpen ? Z_INDEX.GRID_HEADER_MESSAGE_SIDEBAR_OPEN : Z_INDEX.BASE))),
                        cursor: isSortable ? 'pointer' : 'default',
                        width: columnWidth,
                        minWidth: columnWidth,
                        maxWidth: columnWidth,
                      }}
                      ref={(headRef) => {
                        // Ref callback for potential future debugging
                      }}
                      onClick={e => {
                        if (!isSortable) return;
                        // Don't sort if clicking on resize handle or filter icon
                        if (!(e.target as HTMLElement).closest('.filter-icon-btn') && 
                            !(e.target as HTMLElement).closest('.resize-handle')) {
                          handleSortClick(column.columnCode);
                        }
                      }}
                      onDragOver={(e) => {
                        // Always allow drop; we'll validate payload on drop
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        let hasPinPayload = false;
                        try {
                          const data = e.dataTransfer.getData('application/x-workflow-pin');
                          hasPinPayload = !!data;
                        } catch {}
                        if (hasPinPayload) {
                          setPinnedColumn(column.fieldConfigurationId);
                        }
                      }}
                      onContextMenu={(e) => handleColumnHeaderRightClick(e, column.fieldConfigurationId)}
                    >
                      <div className="w-full flex items-center" style={{ minHeight: 24 }}
                           onMouseEnter={() => setHoveredHeader(column.columnCode)}
                           onMouseLeave={() => setHoveredHeader(null)}>
                        <div 
                          className="flex items-center justify-center"
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, minWidth: 0 }}
                          title={getLocalizedField(column, 'columnName', language)}
                        >
                          {isPinned && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className="flex items-center justify-center"
                                    style={{ width: 16, height: 16, marginRight: 4, opacity: 1, transition: 'transform 0.2s ease, opacity 0.2s ease', transform: 'scale(1)', cursor: 'grab' }}
                                    draggable
                                    onDragStart={(e) => {
                                      try { e.dataTransfer.setData('application/x-workflow-pin', column.fieldConfigurationId); } catch {}
                                    }}
                                    onDoubleClick={(e) => { e.stopPropagation(); setPinnedColumn(null); }}
                                    onClick={(e) => { e.stopPropagation(); setPinnedColumn(null); }}
                                  >
                                    <Pin size={16} className="text-blue-600" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('unpinTooltip' as any)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          
                          {/* Private column indicator */}
                          {column.isPrivate && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="ml-1 p-1 rounded hover:bg-gray-100 transition-opacity"
                                  aria-label={t('updateColumnAccess')}
                                  aria-haspopup="dialog"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Lock size={14} className="text-gray-500" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="center" sideOffset={8} collisionPadding={8} className="w-64 p-4" onClick={e => e.stopPropagation()}>
                                <div className="space-y-3">
                                  <p className="font-medium text-sm">{t('whoCanViewColumn')}</p>
                                  {column.authorizedUserList && column.authorizedUserList.length > 0 ? (
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {column.authorizedUserList.map((user: any, idx: number) => (
                                        <div 
                                          key={idx} 
                                          className="flex items-center gap-2 text-xs text-gray-700 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                                          title={user.email || user.fullName}
                                        >
                                          <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                                            {getUserInitials(user)}
                                          </div>
                                          <span className="truncate">{user.fullName || user.email}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-500">{t('noUsersSelected')}</p>
                                  )}
                                  {(() => {
                                    const authorizedActions = (configuration?.authorizedActions || []).map((a: any) => typeof a === 'string' ? a : a.actionCode);
                                    const canEditColumns = authorizedActions.includes("EditColumns");
                                    return canEditColumns ? (
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="w-full mt-2" 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditFieldAccess(column);
                                        }}
                                      >
                                        {t('updateColumnAccess')}
                                      </Button>
                                    ) : null;
                                  })()}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                          
                          {/* Note info indicator */}
                          {column.displayNote && column.note && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="ml-1 p-1 rounded hover:bg-gray-100 transition-opacity"
                                  aria-label={t('note')}
                                  aria-haspopup="dialog"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Info size={14} className="text-blue-500" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="center" sideOffset={8} collisionPadding={8} className="w-80 p-4" onClick={e => e.stopPropagation()}>
                                <div 
                                  className="text-sm text-gray-700 whitespace-pre-wrap break-words"
                                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(column.note || '') }} 
                                />
                              </PopoverContent>
                            </Popover>
                          )}
                          
                          {/* Render content based on column type */}
                          {column.isActionColumn && (column.actionType === 'history' || column.actionType === 'message') ? (
                            // Action columns (history, message) - show icon only
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center justify-center" aria-label={column.actionType === 'history' ? t('viewHistory') : t('viewMessages')}>
                                    {column.actionType === 'history' ? (
                                      <Clock className="h-4 w-4 text-gray-500" />
                                    ) : (
                                      <MessageCircle className="h-4 w-4 text-gray-500" />
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{column.actionType === 'history' ? t('viewHistory') : t('viewMessages')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            // Regular data columns
                            <>
                              <span 
                                className="truncate" 
                                style={{ 
                                  lineHeight: '1.2', 
                                  textAlign: 'center',
                                  color: column.mappingSource ? '#9333ea' : (column.isCustomField ? '#2563eb' : undefined) // Purple for mapped fields, blue for custom fields
                                }}
                              >
                                {getLocalizedField(column, 'columnName', language)}
                              </span>
                              {/* Only show arrow on hover or if sorted */}
                              {isSortable && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', width: 16, height: 16, flexShrink: 0, marginLeft: 4 }}>
                                  {sortState.columnCode === column.columnCode ? (
                                    sortState.direction === 'asc' ? <ArrowUp size={16} className="text-blue-600" /> : <ArrowDown size={16} className="text-blue-600" />
                                  ) : (
                                    hoveredHeader === column.columnCode && <ArrowUp size={16} className="text-gray-300" />
                                  )}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <span className="flex items-center gap-1" style={{ minWidth: showFilterIcon ? 32 : 0, flexShrink: 0 }}>
                          {/* Filter icon (if available) */}
                          {showFilterIcon && (
                            <Popover open={activeFilterColumn === column.columnCode} onOpenChange={(open) => { if (!open) handlePopoverClose() }}>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className={`ml-1 p-1 rounded hover:bg-gray-100 transition-opacity filter-icon-btn ${filtered ? 'text-blue-600' : 'text-gray-400'}`}
                                  tabIndex={-1}
                                  onClick={e => { e.stopPropagation(); handleFilterIconClick(e, column.columnCode); }}
                                >
                                  <CustomFilterIcon size={16} />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="center" sideOffset={8} collisionPadding={8} className="w-64 p-4" onClick={e => e.stopPropagation()} style={{ fontFamily: 'Roboto, sans-serif', maxHeight: '60vh', overflowY: 'auto' }}>
                                <div className="flex flex-col gap-3">
                                  {/* Search input for filtering options */}
                                  {(column.isFacetable || isFilterableMenu) && (
                                    <Input
                                      value={popoverSearch}
                                      onChange={e => setPopoverSearch(e.target.value)}
                                      placeholder={t('search')}
                                      autoFocus
                                      className="mb-2 focus:border-gray-300 focus:outline-none"
                                      onClick={e => e.stopPropagation()}
                                    />
                                  )}
                                  {/* Facet filter (isFacetable) */}
                                  {column.isFacetable ? (
                                    (() => {
                                      // If column has codeValueSource, use code values instead of facets
                                      const useCodeValues = column.codeValueSource && codeValues && codeValues.isReady;
                                      const facetOptions = useCodeValues 
                                        ? getFilterOptionsForColumn(column).map((opt: any) => ({
                                            value: opt.code,
                                            count: undefined,
                                            code: opt.code,
                                            titleEnglish: opt.titleEnglish,
                                            titleFrench: opt.titleFrench,
                                            color: opt.color,
                                            isDeleted: opt.isDeleted
                                          }))
                                        : getFacetOptionsForColumn(stageFacets, column);
                                      const rawValue = popoverFilterValues[column.columnCode];
                                      const selectedValues = Array.isArray(rawValue) ? rawValue : [];
                                      const shouldScroll = facetOptions.length > 4;
                                      // Filter options by search (only by label)
                                      const filteredOptions = popoverSearch.trim()
                                        ? facetOptions.filter((opt: any) => {
                                            const label = getOptionLabel(opt, column, sortedAssignedUsers, language);
                                            return label.toLowerCase().includes(popoverSearch.trim().toLowerCase());
                                          }).sort((a: any, b: any) => {
                                            // Sort alphabetically by label after filtering
                                            const labelA = getOptionLabel(a, column, sortedAssignedUsers, language);
                                            const labelB = getOptionLabel(b, column, sortedAssignedUsers, language);
                                            return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
                                          })
                                        : facetOptions;
                                      
                                      // Add (blank) option to the top of the list
                                      const allOptions = [
                                        { value: "__BLANK__", count: undefined, label: t('blank') },
                                        ...filteredOptions
                                      ];
                                      
                                      // Always show at least the (blank) option, even if no facet options are available
                                      if (facetOptions.length === 0) {
                                        const rawValue = popoverFilterValues[column.columnCode];
                                        const selectedValues = Array.isArray(rawValue) ? rawValue : [];
                                        return (
                                          <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 240 }}>
                                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                              <label className="flex items-center gap-2 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                <Checkbox
                                                  checked={selectedValues.includes('__BLANK__')}
                                                  onCheckedChange={() => {
                                                    const next = selectedValues.includes('__BLANK__')
                                                      ? selectedValues.filter((v: string) => v !== '__BLANK__')
                                                      : [...selectedValues, '__BLANK__'];
                                                    setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: next }));
                                                  }}
                                                />
                                                <span>{t('blank')}</span>
                                              </label>
                                            </div>
                                            <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                              <div className="flex gap-2">
                                                <Button variant="outline" size="sm" onClick={e => {
                                                  e.stopPropagation();
                                                  handlePopoverClose();
                                                }} style={{ flex: 1 }}>
                                                  {t('apply')}
                                                </Button>
                                                <Button variant="destructive" size="sm" onClick={e => {
                                                  e.stopPropagation();
                                                  handleClearColumnFilter(column);
                                                }} style={{ flex: 1 }}>
                                                  {t('clearAll')}
                                                </Button>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                      
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: shouldScroll ? 240 : undefined }}>
                                          <div style={{ flex: 1, overflowY: shouldScroll ? 'auto' : undefined }}>
                                            {allOptions.map((opt: any) => {
                                              const value = getOptionValue(opt);
                                              let user = null;
                                              if (column.columnCode === 'AssignedToUser' && Array.isArray(sortedAssignedUsers)) {
                                                user = sortedAssignedUsers.find(u => u.email?.toLowerCase() === value.toLowerCase());
                                              }
                                              const label = opt.label || getOptionLabel(opt, column, sortedAssignedUsers, language);
                                              const checked = selectedValues.includes(value);
                                              if (!label) return null;
                                              const tooltip = user && user.email ? user.email : undefined;
                                              return (
                                                <label key={value} className="flex items-center gap-2 cursor-pointer" onClick={e => e.stopPropagation()} title={tooltip}>
                                                  <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => {
                                                      const next = checked
                                                        ? selectedValues.filter((v: string) => v !== value)
                                                        : [...selectedValues, value];
                                                      setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: next }));
                                                    }}
                                                  />
                                                  <span>{label} {'count' in opt && opt.count !== undefined ? `(${opt.count})` : ''}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                          <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                            <div className="flex gap-2">
                                              <Button variant="outline" size="sm" onClick={e => {
                                                e.stopPropagation();
                                                handlePopoverClose();
                                              }} style={{ flex: 1 }}>
                                                {t('apply')}
                                              </Button>
                                              <Button variant="destructive" size="sm" onClick={e => {
                                                e.stopPropagation();
                                                handleClearColumnFilter(column);
                                              }} style={{ flex: 1 }}>
                                                {t('clearAll')}
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()
                                  ) : isFilterableMenu && !column.isFacetable ? (
                                    (() => {
                                      const shouldScroll = filterOptions.length > 4;
                                      // Filter options by search (only by label)
                                      const filteredOptions = popoverSearch.trim()
                                        ? filterOptions.filter((opt: any) => {
                                            const label = getOptionLabel(opt, column, sortedAssignedUsers, language);
                                            return label.toLowerCase().includes(popoverSearch.trim().toLowerCase());
                                          }).sort((a: any, b: any) => {
                                            // Sort alphabetically by label after filtering
                                            const labelA = getOptionLabel(a, column, sortedAssignedUsers, language);
                                            const labelB = getOptionLabel(b, column, sortedAssignedUsers, language);
                                            return labelA.toLowerCase().localeCompare(labelB.toLowerCase());
                                          })
                                        : filterOptions;
                                      
                                      // Add (blank) option to the top of the list
                                      const allOptions = [
                                        { label: t('blank'), value: "__BLANK__", code: "__BLANK__" },
                                        ...filteredOptions
                                      ];
                                      
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: shouldScroll ? 240 : undefined }}>
                                          <div style={{ flex: 1, overflowY: shouldScroll ? 'auto' : undefined }}>
                                            {allOptions.map((opt: any) => {
                                              const code = typeof opt.code === 'string' ? opt.code : String(opt.code ?? '');
                                              const value = getOptionValue(opt);
                                              const label = getOptionLabel(opt, column, sortedAssignedUsers, language);
                                              const rawValue = popoverFilterValues[column.columnCode];
                                              const selectedValues = Array.isArray(rawValue) ? rawValue : [];
                                              const checked = selectedValues.includes(value);
                                              return (
                                                <label key={code} className="flex items-center gap-2 cursor-pointer" onClick={e => e.stopPropagation()}>
                                                  <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => {
                                                      const next = checked
                                                        ? selectedValues.filter((v: string) => v !== value)
                                                        : [...selectedValues, value];
                                                      setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: next }));
                                                    }}
                                                  />
                                                  <span>{label} {'count' in opt && opt.count !== undefined ? `(${opt.count})` : ''}</span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                          <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                            <div className="flex gap-2">
                                              <Button variant="outline" size="sm" onClick={e => {
                                                e.stopPropagation();
                                                handlePopoverClose();
                                              }} style={{ flex: 1 }}>
                                                {t('apply')}
                                              </Button>
                                              <Button variant="destructive" size="sm" onClick={e => {
                                                e.stopPropagation();
                                                handleClearColumnFilter(column);
                                              }} style={{ flex: 1 }}>
                                                {t('clearAll')}
                                              </Button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()
                                  ) : column.isSearchable ? (
                                    <SearchableFieldWithSuggestions column={column} />
                                  ) : isNumberField(column) ? (
                                    <>
                                      <Input
                                        type="number"
                                        value={popoverFilterValues[column.columnCode] || ''}
                                        onChange={e => setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: e.target.value }))}
                                        onKeyDown={e => { if (e.key === 'Enter') handlePopoverClose(); }}
                                        placeholder={t('enterValue')}
                                        autoFocus
                                        onClick={e => e.stopPropagation()}
                                        className="focus:border-gray-300 focus:outline-none"
                                      />
                                      <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                        <div className="flex gap-2">
                                          <Button variant="outline" size="sm" onClick={e => {
                                            e.stopPropagation();
                                            handlePopoverClose();
                                          }} style={{ flex: 1 }}>
                                            {t('apply')}
                                          </Button>
                                          <Button variant="destructive" size="sm" onClick={e => {
                                            e.stopPropagation();
                                            handleClearColumnFilter(column);
                                          }} style={{ flex: 1 }}>
                                            {t('clearAll')}
                                          </Button>
                                        </div>
                                      </div>
                                    </>
                                  ) : isDateField(column) ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 240 }}>
                                      <div style={{ flex: 1, overflowY: 'auto' }}>
                                        <div className="flex flex-col gap-2" onClick={e => e.stopPropagation()}>
                                          <ModernDatePicker value={popoverFilterValues[column.columnCode]?.start || null} onChange={val => setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: { ...(prev[column.columnCode] || {}), start: val } }))} placeholder={t('startDate')} />
                                          <ModernDatePicker value={popoverFilterValues[column.columnCode]?.end || null} onChange={val => setPopoverFilterValues(prev => ({ ...prev, [column.columnCode]: { ...(prev[column.columnCode] || {}), end: val } }))} placeholder={t('endDateOptional')} />
                                        </div>
                                      </div>
                                      <div style={{ borderTop: '1px solid #eee', paddingTop: 8, background: '#fff' }}>
                                        <div className="flex gap-2">
                                          <Button variant="outline" size="sm" onClick={e => {
                                            e.stopPropagation();
                                            handlePopoverClose();
                                          }} style={{ flex: 1 }}>
                                            {t('apply')}
                                          </Button>
                                          <Button variant="destructive" size="sm" onClick={e => {
                                            e.stopPropagation();
                                            handleClearColumnFilter(column);
                                          }} style={{ flex: 1 }}>
                                            {t('clearAll')}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                        </span>
                        {/* Resize handle */}
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize transition-all duration-200 resize-handle"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleResizeStart(e, column.fieldConfigurationId);
                          }}
                          style={{
                            zIndex: Z_INDEX.FILTER_ICON,
                            background: isResizing && resizingColumn === column.fieldConfigurationId 
                              ? 'rgba(75, 85, 99, 0.8)' // Darker grey when selected
                              : 'rgba(156, 163, 175, 0.1)', // Very subtle grey background
                            borderLeft: '1px solid rgba(156, 163, 175, 0.3)',
                            pointerEvents: 'auto',
                            right: '-1px',
                            transition: 'all 0.2s ease',
                            opacity: 0,
                            width: '8px', // Smaller default width
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                            e.currentTarget.style.background = 'rgba(156, 163, 175, 0.6)';
                            e.currentTarget.style.width = '10px'; // Slightly smaller hover width
                          }}
                          onMouseLeave={(e) => {
                            if (!isResizing || resizingColumn !== column.fieldConfigurationId) {
                              e.currentTarget.style.opacity = '0';
                              e.currentTarget.style.background = 'rgba(156, 163, 175, 0.1)';
                              e.currentTarget.style.width = '8px'; // Back to small size
                            }
                          }}
                        />
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </Header>

            <TableBody>
              {/* Show loading spinner only for the rows when isLoadingRows, isLoadingConfiguration, or isLoadingWorkOrders is true */}
              {safeItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={displayColumns.length + 1} className="text-center py-8">
                    {t('noRecordsFound')}
                  </TableCell>
                </TableRow>
              ) : (
                safeItems.map((item: any) => {
                  if (!item.id) return null
                  // Extract FboId and ObId (case-insensitive, fallback to undefined)
                  const fboId = item.FboId ?? item.fboId ?? undefined
                  const obId = item.ObId ?? item.obId ?? undefined
                  const hasRowActions = !!fboId || !!obId
                  const isChecked = selectedItems.includes(item.id);
                  
                  // Check if context menu should be enabled
                  // Disable when multiple items are selected or on mobile
                  const isContextMenuEnabled = hasRowActions && selectedItems.length <= 1 && isDesktop();
                  
                  return (
                    <TableRow
                      key={item.id}
                      onContextMenu={isContextMenuEnabled ? (e) => {
                        e.preventDefault()
                        
                        // Track context menu opening
                        trackWorkflowEvent('Click Tracking', {
                          action: 'context_menu_open',
                          rowId: item.id,
                          recordType: item.recordType,
                          stage: stage
                        });
                        
                        setRowActionMenu({
                          open: true,
                          fboId,
                          obId,
                          rowId: item.id,
                          x: e.clientX,
                          y: e.clientY,
                        })
                      } : undefined}
                      style={{
                        cursor: isContextMenuEnabled ? 'context-menu' : undefined,
                        borderBottom: '1px solid #ECECEC',
                        height: 40,
                        minHeight: 40,
                        maxHeight: 40,
                      }}
                    >
                      {/* Checkbox cell */}
                      <TableCell 
                        className="text-center" 
                        style={{ 
                          width: 50, 
                          minWidth: 50,
                          maxWidth: 50,
                          padding: '6px 0',
                          position: 'sticky',
                          left: 0,
                          zIndex: isMessageSidebarOpen ? Z_INDEX.FIXED_COLUMN_MESSAGE_SIDEBAR_OPEN : Z_INDEX.FIXED_CELL,
                          background: isMessageSidebarOpen ? 'rgba(255, 255, 255, 0.6)' : '#fff',
                          borderRight: '1px solid #ECECEC',
                          borderBottom: '1px solid rgb(236, 236, 236)'
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleItemSelection(item.id)}
                          aria-label={isChecked ? 'Deselect row' : 'Select row'}
                        />
                      </TableCell>
                      
                      {/* Render all dynamic columns */}
                      {displayColumns.map((column) => {
                        const cellValue = getCellValue(item, column);
                        const columnWidth = getSafeColumnWidth(columnWidths, column.fieldConfigurationId);
                        const currentStageConfig = stages.find(s => s.stageCode.toLowerCase() === (stage || '').toLowerCase());
                        const pinnedMap = state.pinnedColumnsByStage || {};
                        const pinnedId = currentStageConfig ? pinnedMap[currentStageConfig.stageId] : undefined;
                        const isSticky = pinnedId ? (displayColumns.findIndex(c => c.fieldConfigurationId === column.fieldConfigurationId) <= displayColumns.findIndex(c => c.fieldConfigurationId === pinnedId)) : false;
                        let leftOffset = 50; // checkbox width
                        if (isSticky) {
                          const currentIndex = displayColumns.findIndex(c => c.fieldConfigurationId === column.fieldConfigurationId);
                          for (let i = 0; i < currentIndex; i++) {
                            leftOffset += getSafeColumnWidth(columnWidths, displayColumns[i].fieldConfigurationId);
                          }
                        }

                        let cellColor = undefined;
                        if (column.color) {
                          cellColor = column.color;
                        }
                        else if (column.customValues && cellValue) {
                          const match = column.customValues.find((cv: any) => cv.code === cellValue.toString());
                          if (match && match.color) cellColor = match.color;
                        }
                        // Boolean display mapping
                        let displayValue = cellValue;
                        if (column.columnTypeCode?.toLowerCase() === 'boolean') {
                          if (cellValue === true) {
                            displayValue = getLocalizedField(defaultBooleanOptions.find(opt => opt.code === 'true') || {}, 'title', language);
                          } else if (cellValue === false) {
                            displayValue = getLocalizedField(defaultBooleanOptions.find(opt => opt.code === 'false') || {}, 'title', language);
                          } else {
                            displayValue = '';
                          }
                        }
                        // Handle CodeValueSource for display value
                        else if (column.codeValueSource && codeValues.isReady && cellValue) {
                          try {
                            // Handle TextList (array values)
                            if (column.columnTypeCode?.toLowerCase() === 'textlist' && Array.isArray(cellValue)) {
                              const displayTexts = cellValue.map((code: any) => {
                                const codeStr = String(code);
                                const text = codeValues.getCodeValueTextByCode(codeStr, column.codeValueSource);
                                return text || codeStr;
                              });
                              displayValue = displayTexts.join(', ');
                            } else {
                              // Handle single value
                              const codeValueText = codeValues.getCodeValueTextByCode(String(cellValue), column.codeValueSource);
                              if (codeValueText) {
                                displayValue = codeValueText;
                              }
                            }
                          } catch (error) {
                            console.warn('Failed to get code value text for display:', error);
                          }
                        }
                        // Handle objects (with or without subColumnName)
                        else if (typeof cellValue === 'object' && cellValue !== null) {
                          displayValue = getDisplayValueFromColumn(cellValue, column);
                        }
                        return (
                          <TableCell
                            key={`${item.id}-${column.fieldConfigurationId}`}
                            style={{
                              borderRight: '1px solid #ECECEC',
                              borderBottom: '1px solid #ECECEC',
                              padding: '6px 12px',
                              fontSize: 14,
                              verticalAlign: 'middle',
                              width: columnWidth,
                              minWidth: columnWidth,
                              maxWidth: columnWidth,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              backgroundColor: isSticky ? (cellColor || '#fff') : (column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen ? 'rgba(255, 255, 255, 0.6)' : (cellColor || undefined)),
                              textAlign: 'center',
                              fontWeight: cellColor ? 500 : undefined,
                              position: isSticky ? 'sticky' : undefined,
                              left: isSticky ? leftOffset : undefined,
                              zIndex: isSticky ? Z_INDEX.CELL_CONTENT : (column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen && clickedMessageRowId === item.id ? Z_INDEX.MESSAGE_CELL_ACTIVE : (column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen ? Z_INDEX.MESSAGE_CELL_MESSAGE_SIDEBAR_OPEN : (column.isActionColumn && column.actionType === 'message' ? Z_INDEX.MESSAGE_CELL_DEFAULT : undefined))),
                            }}
                            ref={(cellRef) => {
                            }}
                          >
                            <div 
                              className="w-full overflow-hidden text-ellipsis whitespace-nowrap"
                              style={{ width: '100%' }}
                            >
                              {column.isActionColumn && (column.actionType === 'history' || column.actionType === 'message') ? (
                                // Action columns (history, message) - render action buttons
                                <div className="flex items-center justify-center">
                                  {column.actionType === 'history' ? (
                                    <button
                                      className="p-1 hover:bg-gray-100 rounded transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const itemTitle = item.workerFullName || item.jobTitle || item.clientName || 'Workflow Item';
                                        setAuditHistoryPanel({
                                          isOpen: true,
                                          itemId: item.id,
                                          itemTitle
                                        });
                                      }}
                                      title={t('viewHistory')}
                                    >
                                      <Clock className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                                    </button>
                                  ) : column.actionType === 'message' ? (
                                    <div className="flex items-center justify-center">
                                      {item.workflowHubId && (
                                        <MessageTrigger 
                                          workflowHubId={String(item.workflowHubId)} 
                                          size="sm"
                                          zIndex={isSticky ? Z_INDEX.CELL_CONTENT : (column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen && clickedMessageRowId === item.id ? Z_INDEX.MESSAGE_CELL_ACTIVE : (column.isActionColumn && column.actionType === 'message' && isMessageSidebarOpen ? Z_INDEX.MESSAGE_CELL_MESSAGE_SIDEBAR_OPEN : (column.isActionColumn && column.actionType === 'message' ? Z_INDEX.MESSAGE_CELL_DEFAULT : undefined)))}
                                          onMessageClick={() => setClickedMessageRowId(item.id)}
                                          onSidebarStateChange={(isOpen) => {
                                            setIsMessageSidebarOpen(isOpen);
                                            if (!isOpen) setClickedMessageRowId(null);
                                          }}
                                        />
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              ) : (
                                <GridCell
                                  order={item}
                                  column={column}
                                  stage={stage as WorkflowStage}
                                  isEditing={editingCell?.rowId === item.id && editingCell?.columnId === column.fieldConfigurationId}
                                  onEdit={() => handleCellEdit(item.id!, column.fieldConfigurationId)}
                                  onSave={(value) => handleCellSave(item.id!, column.columnCode, value)}
                                  onCancel={() => setEditingCell(null)}
                                  fieldNameOverride={column.columnCode}
                                  canEditFieldData={canEditFieldData}
                                />
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
          {(isLoadingRows || isLoadingConfiguration || isLoadingWorkOrders) && (
            <div
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(255,255,255,0.6)',
                zIndex: Z_INDEX.AUDIT_PANEL,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'all'
              }}
            >
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                <span className="text-gray-700 font-medium">{t('loading')}</span>
              </div>
            </div>
          )}
          
          {/* Resize indicator */}
          {isResizing && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.1)',
                zIndex: Z_INDEX.AUDIT_PANEL_BACKDROP,
                pointerEvents: 'none',
                cursor: 'col-resize'
              }}
            />
          )}
          

        </div>
      </div>

      {/* Pagination below the table */}
      <div ref={paginationRef} className="flex justify-between mt-2" style={{ width: '100%' }}>
        {/* Left side - Rows per page selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{t('rowsPer')}</span>
          <select
            value={state.itemsPerPage === null ? 'auto' : state.itemsPerPage.toString()}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'auto') {
                setItemsPerPage(null);
              } else {
                setItemsPerPage(Number(value));
              }
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            style={{ minWidth: '80px' }}
          >
            <option value="auto">Auto</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        
        {/* Right side - Showing records and pagination navigation */}
        <div className="flex items-center gap-4">
          {/* Showing records */}
          <div className="text-sm text-gray-500">
            {t('showingRecords', { 
              start: (currentPage - 1) * (state.itemsPerPage !== null ? state.itemsPerPage : pageSize) + 1, 
              end: Math.min((currentPage - 1) * (state.itemsPerPage !== null ? state.itemsPerPage : pageSize) + (state.itemsPerPage !== null ? state.itemsPerPage : pageSize), totalCount), 
              total: totalCount 
            })}
          </div>
          
          {/* Pagination navigation */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={14} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1"
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      </div>

      {isEditColumnsOpen && <EditColumns isOpen={isEditColumnsOpen} onClose={() => setIsEditColumnsOpen(false)} />}

      {isBrowser && contextMenu && (
        <ColumnContextMenu
          position={contextMenu.position}
          columnId={contextMenu.columnId}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Right-click row action menu (popover style) */}
      {rowActionMenu?.open && (
        <>
          <div
            style={{
              position: 'fixed',
              top: rowActionMenu.y + 2,
              left: rowActionMenu.x + 2,
              zIndex: Z_INDEX.AUDIT_PANEL_CONTENT,
              minWidth: 220,
              background: 'white',
              borderRadius: 6,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              border: '1px solid #e5e7eb',
              padding: 0,
            }}
            onContextMenu={e => e.preventDefault()}
          >
            <div className="flex flex-col divide-y divide-gray-100">
              {/* History option */}
              {(() => {
                // Find the row by rowId
                const row = safeItems.find(i => i.id === rowActionMenu.rowId);
                
                return (
                  <button
                    className="text-left w-full px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-center space-x-2"
                    onClick={() => {
                      // Track context menu item selection
                      trackWorkflowEvent('Click Tracking', {
                        action: 'context_menu_item_selected',
                        sourceCode: 'history',
                        rowId: rowActionMenu.rowId,
                        recordType: row?.recordType,
                      });
                      
                      // Open audit history panel
                      const itemTitle = row?.workerFullName || row?.jobTitle || row?.clientName || 'Workflow Item';
                      setAuditHistoryPanel({
                        isOpen: true,
                        itemId: rowActionMenu.rowId,
                        itemTitle
                      });
                      setRowActionMenu(null);
                    }}
                    type="button"
                  >
                    <span>{t('viewHistory')}</span>
                  </button>
                );
              })()}
              
              {/* Dynamic external source links */}
              {(() => {
                // Find the row by rowId
                const row = safeItems.find(i => i.id === rowActionMenu.rowId)
                
                if (!row) {
                  return (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      {t('noActionsAvailable')}
                    </div>
                  )
                }
                
                // Process external sources using the new utility function
                // Condition evaluation handles which sources should be displayed
                const validSources = processExternalSources(externalSources, row)
                
                // If no valid sources, show a message
                if (validSources.length === 0 && !rowActionMenu?.fboId) {
                  return (
                    <div className="px-4 py-3 text-gray-500 text-sm">
                      {t('noActionsAvailable')}
                    </div>
                  )
                }
                
                return validSources.map((externalSource, index) => (
                  <button
                    key={`${externalSource.titleEnglish}-${index}`}
                    className="text-left w-full px-4 py-3 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    onClick={() => {
                      // Track context menu item selection
                      trackWorkflowEvent('Click Tracking', {
                        action: 'context_menu_item_selected',
                        title: getLocalizedField(externalSource, 'title', language),
                        rowId: rowActionMenu.rowId,
                        recordType: row?.recordType,
                      });
                      
                      // Use WindowRefService for consistent window opening
                      WindowRefService.openUrl(externalSource.url);
                      setRowActionMenu(null);
                    }}
                    type="button"
                  >
                    {getLocalizedField(externalSource, 'title', language)}
                  </button>
                ))
              })()}
            </div>
          </div>
          {/* Overlay to close menu on click outside */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: Z_INDEX.AUDIT_PANEL_OVERLAY }}
            onClick={() => setRowActionMenu(null)}
            onContextMenu={e => {
              e.preventDefault();
              setRowActionMenu(null);
            }}
          />
        </>
      )}

      {/* Global Filter Sidebar */}
      {drawerVisible && (
        <>
          {/* Overlay for click outside */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: Z_INDEX.AUDIT_PANEL_CLOSE,
              background: 'transparent',
            }}
            onClick={() => setIsGlobalFilterSidebarOpen(false)}
          />
          <div
            style={{
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: 16,
              gap: 24,
              width: FILTER_DRAWER_WIDTH,
              height: '100vh',
              background: '#FFFFFF',
              border: '1px solid rgba(2, 1, 13, 0.1)',
              borderRadius: 16,
              position: 'fixed',
              top: 0,
              right: 0,
              zIndex: Z_INDEX.AUDIT_PANEL_HEADER,
              boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
              transform: isGlobalFilterSidebarOpen ? 'translateX(0)' : `translateX(${FILTER_DRAWER_WIDTH}px)`,
              opacity: isGlobalFilterSidebarOpen ? 1 : 0,
              transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s cubic-bezier(0.4,0,0.2,1)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 22, color: '#02010D99' }}>
                {t('filter')}{selectedSectionCount > 0 ? ` (${selectedSectionCount})` : ''}
                </span>
                <button onClick={() => setIsGlobalFilterSidebarOpen(false)} style={{ background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#23213D', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ width: '100%', height: 1, background: 'rgba(2, 1, 13, 0.1)', margin: '8px 0 16px 0' }} />
              {/* Search bar */}
              <div style={{ width: '100%', marginBottom: 16 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: '#fff',
                  border: '1px solid #E0E3EB',
                  borderRadius: 8,
                  padding: '0 12px',
                  height: 40,
                  width: '100%',
                }}>
                  <input
                    type="text"
                    placeholder={t('search')}
                    style={{
                      border: 'none',
                      outline: 'none',
                      flex: 1,
                      fontSize: 16,
                      background: 'transparent',
                    }}
                    value={globalFilterSearch}
                    onChange={e => setGlobalFilterSearch(e.target.value)}
                  />
                  <svg width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', marginLeft: 4 }}>
                    <path d="M15.5 15.5l-3-3m1.5-5A6 6 0 1 1 3 3a6 6 0 0 1 11 4.5Z" stroke="#9795B5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', width: '100%' }}>
              {/* List global filter fields */}
              {getFilteredGlobalFilterFields().map(({ field, options }) => {
                const showCount = globalFilterShowCount[field.fieldConfigurationId] || 3;
                const selected = globalFilterSelections[field.fieldConfigurationId] || [];
                const facetOptions = getFacetOptionsForColumnSidebar(facets, field);
                // For Select All logic, use filtered options when searching, otherwise use all facetOptions
                const optionsForSelectAll = globalFilterSearch.trim() ? options : facetOptions;
                const allOptionValues = [
                  ...optionsForSelectAll.map((opt: { value: string }) => opt.value),
                  '__BLANK__'
                ];
                const hasSelectAll = selected.length === allOptionValues.length && 
                                   allOptionValues.every(val => selected.includes(val));
                const hasSomeSelected = selected.length > 0;
                
                // Determine the state of the Select All checkbox
                let selectAllChecked: boolean | 'indeterminate';
                if (hasSelectAll) {
                  selectAllChecked = true;
                } else if (hasSomeSelected) {
                  selectAllChecked = 'indeterminate';
                } else {
                  selectAllChecked = false;
                }
                
                // Use filtered options from search, not unfiltered facetOptions
                // Map options with labels first
                const optionsWithLabels = options.map((opt: { value: string; count: number }) => {
                  let label = getOptionLabel(opt, field, sortedAssignedUsers, language);
                  return { ...opt, label };
                });
                
                // Sort options: selected items first, then unselected, each group sorted alphabetically
                const sortedOptions = optionsWithLabels.sort((a, b) => {
                  const aSelected = selected.includes(a.value);
                  const bSelected = selected.includes(b.value);
                  
                  // If selection status is different, selected items come first
                  if (aSelected !== bSelected) {
                    return aSelected ? -1 : 1;
                  }
                  
                  // If both have same selection status, sort alphabetically by label
                  return (a.label || '').localeCompare(b.label || '');
                });
                
                return (
                  <div key={field.fieldConfigurationId} style={{ marginBottom: 24, width: '100%' }}>
                    <div style={{ fontWeight: 700, marginBottom: 8, color: '#23213D', fontSize: 16 }}>{getLocalizedField(field, 'columnName', language)}{selected.length > 0 ? ` (${selected.length})` : ''}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {/* Select All option */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 16, color: '#23213D', fontWeight: 400 }}>
                        <Checkbox
                          checked={selectAllChecked}
                          onCheckedChange={() => handleGlobalCheckboxChange(field.fieldConfigurationId, '__SELECT_ALL__')}
                        />
                        <span style={{ fontSize: 16, color: '#23213D', fontWeight: 400 }}>Select all</span>
                      </label>
                      
                      {/* Separator line after Select all */}
                      <div style={{ width: '100%', height: 1, background: 'rgba(2, 1, 13, 0.1)', margin: '4px 0' }} />
                      
                      {/* (blank) option */}
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 16, color: '#23213D', fontWeight: 400 }}>
                        <Checkbox
                          checked={selected.includes('__BLANK__')}
                          onCheckedChange={() => handleGlobalCheckboxChange(field.fieldConfigurationId, '__BLANK__')}
                        />
                        <span style={{ fontStyle: 'italic', color: '#666' }}>{t('blank')}</span>
                      </label>
                      
                      {/* Regular options */}
                      {sortedOptions.slice(0, showCount).map(opt => (
                        <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 16, color: '#23213D', fontWeight: 400 }}>
                          <Checkbox
                            checked={selected.includes(opt.value)}
                            onCheckedChange={() => handleGlobalCheckboxChange(field.fieldConfigurationId, opt.value)}
                          />
                          <span>{opt.label} {'count' in opt && opt.count !== undefined ? `(${opt.count})` : ''}</span>
                        </label>
                      ))}
                      {sortedOptions.length > showCount && (
                        <button
                          style={{ background: 'none', border: 'none', color: '#2B345E', cursor: 'pointer', padding: 0, marginTop: 4, fontSize: 15, fontWeight: 500 }}
                          onClick={() => handleShowMore(field.fieldConfigurationId, sortedOptions.length)}
                        >
                          {t('showMore')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Bottom buttons */}
            <div style={{ width: '100%', marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{ width: '100%', height: 1, background: 'rgba(2, 1, 13, 0.1)', margin: '0 0 0 0' }} />
              <button
                style={{
                  width: '100%',
                  height: 48,
                  background: 'none',
                  border: 'none',
                  color: '#2B345E',
                  fontWeight: 500,
                  fontSize: 17,
                  cursor: 'pointer',
                  textAlign: 'center',
                  margin: 0,
                  padding: 0,
                  transition: 'color 0.2s',
                  borderBottom: '1px solid #E0E3EB',
                  borderRadius: 0,
                  letterSpacing: '0.1px',
                }}
                onClick={() => {
                  handleGlobalApply();
                }}
              >
                {t('apply')}
              </button>
              <button
                style={{
                  width: '100%',
                  height: 48,
                  background: 'none',
                  border: 'none',
                  color: '#B71C1C',
                  fontWeight: 500,
                  fontSize: 17,
                  cursor: 'pointer',
                  textAlign: 'center',
                  margin: 0,
                  padding: 0,
                  transition: 'color 0.2s',
                  borderRadius: 0,
                  letterSpacing: '0.1px',
                }}
                onClick={() => {
                  handleGlobalClearAll();
                }}
              >
                {t('clearAll')}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        selectedItems={selectedItems}
        bulkUpdateReports={useWorkflowApi().bulkUpdateReports}
        updateWorkOrdersOptimistically={updateWorkOrdersOptimistically}
        setSelectedItems={setSelectedItems}
        t={t as (key: string) => string}
        fields={editableFields}
        language={language}
        assignedUsers={sortedAssignedUsers}
        refreshData={refreshData}
      />
      {/* Move To Modal (to be implemented) */}
      <MoveToModal
        isOpen={isMoveToOpen}
        onClose={() => setIsMoveToOpen(false)}
        selectedItems={selectedItems}
        configuration={configuration}
        currentStage={stage}
        refreshData={refreshData}
        setSelectedItems={setSelectedItems}
        language={language}
        t={t as (key: string) => string}
        currentOrderBy={currentOrderBy}
      />

      {/* Audit History Panel */}
      {auditHistoryPanel && (
        <AuditHistoryPanel
          isOpen={auditHistoryPanel.isOpen}
          onClose={() => setAuditHistoryPanel(null)}
          itemId={auditHistoryPanel.itemId}
          itemTitle={auditHistoryPanel.itemTitle}
        />
      )}

      {/* Checklist Panel */}
      {checklistPanelState && (
        <ChecklistPanel
          isOpen={checklistPanelState.isOpen}
          onClose={() => setChecklistPanelState(null)}
          itemId={checklistPanelState.itemId}
          workOrderId={checklistPanelState.workOrderId}
          fieldConfigurationId={checklistPanelState.fieldConfigurationId}
          itemTitle={checklistPanelState.itemTitle}
          columnTitle={(() => {
            const column = displayColumns.find(col => col.fieldConfigurationId === checklistPanelState.fieldConfigurationId);
            return column ? getLocalizedField(column, 'columnName', language) : 'Checklist';
          })()}
          canEdit={checklistPanelState.canEdit ?? true}
          initialItems={(() => {
            const workOrder = workOrders.find(wo => wo.id === checklistPanelState.itemId);
            if (!workOrder) return [];
            const column = displayColumns.find(col => col.fieldConfigurationId === checklistPanelState.fieldConfigurationId);
            if (!column) return [];
            const value = getCellValue(workOrder, column);
            try {
              if (Array.isArray(value)) {
                // Backend returns array of items directly
                return value;
              } else if (value && typeof value === 'object' && value.items) {
                // Legacy format with items property
                return value.items;
              } else if (value && typeof value === 'string') {
                const parsed = JSON.parse(value);
                // Check if parsed is array or has items property
                if (Array.isArray(parsed)) {
                  return parsed;
                }
                return parsed.items || [];
              }
            } catch (error) {
              console.warn('Failed to parse checklist data:', error);
            }
            return [];
          })()}
          onSave={async (items) => {
            try {
              // Find the column to get the columnCode
              const column = displayColumns.find(col => col.fieldConfigurationId === checklistPanelState.fieldConfigurationId);
              if (column) {
                // Send the items array directly
                await handleCellSave(checklistPanelState.itemId, column.columnCode, items);
              }
            } catch (error) {
              console.error('Failed to save checklist:', error);
            }
          }}
        />
      )}

      {/* Edit Field Modal */}
      {isEditFieldModalOpen && editFieldConfiguration && (
        <EditFieldModal
          isOpen={isEditFieldModalOpen}
          onClose={() => {
            setIsEditFieldModalOpen(false);
            setEditFieldConfiguration(null);
          }}
          fieldConfiguration={editFieldConfiguration}
          addMode={false}
          canEditCustomColumns={true}
        />
      )}

    </div>
  );
}